// API Service for WYNI AI ERP Frontend
import dayjs from 'dayjs';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';

class ApiService {
  private baseURL: string;
  private isRefreshing = false;
  private refreshPromise: Promise<void> | null = null;
  private redirecting = false;
  private lastRefreshAuthErrorAt: number | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  // Generic HTTP methods
  private async request<T>(
      endpoint: string,
      options: RequestInit & { timeoutMs?: number; noTimeout?: boolean } = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    // 自動添加認證 token
    const token = localStorage.getItem('authToken');

    const { headers: optionsHeaders, signal: userSignal, timeoutMs: userTimeoutMs, noTimeout: userNoTimeout, ...restOptions } = options as any;
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...optionsHeaders,
      },
      ...restOptions,
    };

    try {
      // Add timeout control (configurable / disable)
      const isScheduleRun = /^\/schedules\/[^/]+\/run(?:\?.*)?$/.test(endpoint);
      const noTimeout = userNoTimeout === true || isScheduleRun;
      const timeoutMs = typeof userTimeoutMs === 'number' ? Math.max(0, userTimeoutMs) : 30000;

      let controller: AbortController | null = null;
      let timeoutId: any = null;

      if (!noTimeout) {
        controller = new AbortController();
        // propagate external abort to our controller
        if (userSignal) {
          const extSignal: AbortSignal = userSignal as AbortSignal;
          if (extSignal.aborted) {
            controller.abort((extSignal as any).reason);
          } else {
            extSignal.addEventListener('abort', () => controller && controller.abort((extSignal as any).reason), { once: true });
          }
        }
        if (timeoutMs > 0) {
          timeoutId = setTimeout(() => controller && controller.abort(new DOMException('Timeout', 'TimeoutError')), timeoutMs);
        }
      }

      let response = await fetch(url, {
        ...config,
        signal: noTimeout ? (userSignal as AbortSignal | undefined) : controller?.signal
      });

      if (timeoutId) clearTimeout(timeoutId);

      // 未授權處理：嘗試刷新並重試一次
      if (response.status === 401) {
        const refreshed = await this.tryRefreshTokensOnce();
        if (refreshed) {
          const newToken = localStorage.getItem('authToken');
          const retryHeaders: any = {
            'Content-Type': 'application/json',
            ...options.headers,
          };
          if (newToken) retryHeaders['Authorization'] = `Bearer ${newToken}`;

          // retry respects same timeout rules
          let retryController: AbortController | null = null;
          let retryTimeoutId: any = null;
          let retrySignal: AbortSignal | undefined = undefined;
          if (!noTimeout) {
            retryController = new AbortController();
            if (userSignal) {
              const extSignal: AbortSignal = userSignal as AbortSignal;
              if (extSignal.aborted) {
                retryController.abort((extSignal as any).reason);
              } else {
                extSignal.addEventListener('abort', () => retryController && retryController.abort((extSignal as any).reason), { once: true });
              }
            }
            if (timeoutMs > 0) retryTimeoutId = setTimeout(() => retryController && retryController.abort(new DOMException('Timeout', 'TimeoutError')), timeoutMs);
            retrySignal = retryController.signal;
          } else {
            retrySignal = userSignal as AbortSignal | undefined;
          }
          response = await fetch(url, { ...restOptions, headers: retryHeaders, signal: retrySignal });
          if (retryTimeoutId) clearTimeout(retryTimeoutId);
        }
      }

      // 嘗試解析返回數據（無論 ok 與否），以便檢查 TOKEN_INVALID 代碼
      let parsed: any = null;
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        try {
          parsed = await response.json();
        } catch {}
      }

      // 若仍未 ok，且錯誤碼為 TOKEN_INVALID -> 檢查是否需要登出
      if (!response.ok) {
        const code = parsed?.code;
        // 只有在以下情況才強制登出：
        // 1. 明確的 TOKEN_INVALID 代碼
        // 2. 並且沒有 refresh token（已被清除或從未存在）
        // 這樣可以避免在 token 過期但 refresh 還有效時立即登出
        if (code === 'TOKEN_INVALID') {
          const hasRefreshToken = !!localStorage.getItem('refreshToken');
          if (!hasRefreshToken) {
            // 只有在 refresh token 也失效時才強制登出
            this.forceLogoutAndRedirect();
          }
          // 如果還有 refresh token，不立即登出，讓下一次請求自動嘗試刷新
        }
        let errorMessage = `HTTP error! status: ${response.status}`;
        if (parsed?.error) errorMessage = parsed.error;
        else if (parsed?.message) errorMessage = parsed.message;

        // 創建一個包含完整響應信息的錯誤對象
        const error: any = new Error(errorMessage);
        error.response = {
          status: response.status,
          data: parsed || { error: errorMessage }
        };
        throw error;
      }

      // 200 但 body 表示憑證失效（部分端點可能這樣返回）
      if (parsed && parsed.success === false && parsed.code === 'TOKEN_INVALID') {
        const refreshed = await this.tryRefreshTokensOnce();
        if (refreshed) {
          // 成功刷新後重試一次原請求
          const newToken = localStorage.getItem('authToken');
          const retryHeaders: any = {
            'Content-Type': 'application/json',
            ...options.headers,
          };
          if (newToken) retryHeaders['Authorization'] = `Bearer ${newToken}`;

          let retryController: AbortController | null = null;
          let retryTimeoutId: any = null;
          let retrySignal: AbortSignal | undefined = undefined;
          if (!noTimeout) {
            retryController = new AbortController();
            if (userSignal) {
              const extSignal: AbortSignal = userSignal as AbortSignal;
              if (extSignal.aborted) {
                retryController.abort((extSignal as any).reason);
              } else {
                extSignal.addEventListener('abort', () => retryController && retryController.abort((extSignal as any).reason), { once: true });
              }
            }
            if (timeoutMs > 0) retryTimeoutId = setTimeout(() => retryController && retryController.abort(new DOMException('Timeout', 'TimeoutError')), timeoutMs);
            retrySignal = retryController.signal;
          } else {
            retrySignal = userSignal as AbortSignal | undefined;
          }
          const retryResp = await fetch(url, { ...restOptions, headers: retryHeaders, signal: retrySignal });
          if (retryTimeoutId) clearTimeout(retryTimeoutId);

          if (!retryResp.ok) {
            // 重試仍失敗 -> 僅在 refresh token 也失效時才登出
            const retryCt = retryResp.headers.get('content-type') || '';
            const retryJson = retryCt.includes('application/json') ? await retryResp.json().catch(() => null) : null;
            if (retryResp.status === 401 || retryJson?.code === 'TOKEN_INVALID') {
              // 只有在 refresh token 也失效時才強制登出
              const hasRefreshToken = !!localStorage.getItem('refreshToken');
              if (!hasRefreshToken) {
                this.forceLogoutAndRedirect();
              }
            }
            let retryMsg = `HTTP error! status: ${retryResp.status}`;
            if (retryJson?.error) retryMsg = retryJson.error;
            else if (retryJson?.message) retryMsg = retryJson.message;
            throw new Error(retryMsg);
          }
          // 重試成功則返回重試結果
          const retryParsed = await retryResp.json().catch(() => null);
          // @ts-ignore 返回重試解析
          return retryParsed as T;
        }
        // 刷新失敗 -> 只有在 refresh token 也沒有時才強制登出
        const hasRefreshToken = !!localStorage.getItem('refreshToken');
        if (!hasRefreshToken) {
          this.forceLogoutAndRedirect();
        }
        throw new Error(parsed?.error || '認證已失效，請重新登入');
      }

      // 正常成功返回
      if (parsed !== null) {
        // @ts-ignore 保持與以往一致返回 JSON
        return parsed as T;
      }
      // 非 JSON 回應時（罕見）
      // @ts-ignore
      return undefined as T;
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('無法連接到服務器，請檢查網絡連接或後端服務是否運行');
      }
      throw error;
    }
  }

  private async tryRefreshTokensOnce(): Promise<boolean> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) return false;

      // 使用 refreshTokens 方法來統一處理刷新邏輯，避免競態條件
      await this.refreshTokens({ refreshToken });
      return !!localStorage.getItem('authToken');
    } catch (e) {
      return false;
    }
  }

  private shouldClearTokensOnRefreshAuthError(): boolean {
    const now = Date.now();
    if (!this.lastRefreshAuthErrorAt || now - this.lastRefreshAuthErrorAt > 10000) {
      this.lastRefreshAuthErrorAt = now;
      return false; // 首次或超過寬限窗，不清理，避免瞬時抖動
    }
    this.lastRefreshAuthErrorAt = now;
    return true; // 寬限窗內第二次，清理 token
  }

  private forceLogoutAndRedirect() {
    if (this.redirecting) return;
    this.redirecting = true;
    try {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      // 直接跳轉，確保受保護路由被重新評估，並適配 BASE_URL
      const base = ((import.meta as any)?.env?.BASE_URL) || '/';
      const to = (base?.endsWith('/') ? base : `${base}/`) + 'login';
      window.location.href = to;
    } catch {
      // noop
    }
  }

  async get<T>(endpoint: string, opts?: { params?: Record<string, any>; signal?: AbortSignal }): Promise<T> {
    const qp = new URLSearchParams();
    if (opts?.params) {
      Object.entries(opts.params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && String(v).length > 0) qp.append(k, String(v));
      });
    }
    const path = `${endpoint}${qp.toString() ? `?${qp.toString()}` : ''}`;
    return this.request<T>(path, { method: 'GET', signal: opts?.signal });
  }

  // 單參數版本統一走上面的可選參數簽名
  /*async get<T>(endpoint: string): Promise<T> {
  return this.request<T>(endpoint, { method: 'GET' });
  }*/

  async post<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Health check
  async healthCheck() {
    return this.get('/health');
  }

  // ===== 認證相關 API =====

  // 用戶登錄
  async login(credentials: { email: string; password: string }) {
    return this.post('/users/login', credentials);
  }

  // ===== User Management API =====
  
  // Get all users with pagination and filtering
  async getUsers(params?: {
    page?: number;
    limit?: number;
    companyId?: string;
    isActive?: boolean;
    role?: string;
    search?: string;
  }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', params.page.toString());
    if (params?.limit) qp.append('limit', params.limit.toString());
    if (params?.companyId) qp.append('companyId', params.companyId);
    if (params?.isActive !== undefined) qp.append('isActive', params.isActive.toString());
    if (params?.role) qp.append('role', params.role);
    if (params?.search) qp.append('search', params.search);
    const q = qp.toString();
    return this.get(`/users${q ? `?${q}` : ''}`);
  }

  // Get user by ID
  async getUserById(id: string) {
    return this.get(`/users/${encodeURIComponent(id)}`);
  }

  // Create new user (Admin only)
  async createUser(data: {
    email: string;
    password: string;
    name: string;
    role: 'ADMIN' | 'SUPER_ADMIN' | 'CUSTOMER' | 'PICK_SCANNING' | 'PACK_SCANNING' | 'PICKUP_SCANNING';
    companyId?: string;
    customerId?: string;
  }) {
    return this.post('/users', data);
  }

  // Update user
  async updateUser(id: string, data: {
    name?: string;
    email?: string;
    role?: 'ADMIN' | 'SUPER_ADMIN' | 'CUSTOMER' | 'PICK_SCANNING' | 'PACK_SCANNING' | 'PICKUP_SCANNING';
    isActive?: boolean;
    companyId?: string | null;
    customerId?: string | null;
  }) {
    return this.put(`/users/${encodeURIComponent(id)}`, data);
  }

  // Get customers list for dropdown
  async getCustomersList(params?: { companyId?: string; search?: string }) {
    const qp = new URLSearchParams();
    if (params?.companyId) qp.append('companyId', params.companyId);
    if (params?.search) qp.append('search', params.search);
    const q = qp.toString();
    return this.get(`/users/customers/list${q ? `?${q}` : ''}`);
  }

  // Delete user (soft delete)
  async deleteUser(id: string) {
    return this.delete(`/users/${encodeURIComponent(id)}`);
  }

  // Reset user password (Admin only)
  async resetUserPassword(data: { email: string; newPassword: string }) {
    return this.post('/users/reset-password', data);
  }

  // Change own password
  async changePassword(data: { currentPassword: string; newPassword: string }) {
    return this.post('/users/change-password', data);
  }

  // Get current user profile
  async getUserProfile() {
    return this.get('/users/profile');
  }

  // Update current user profile
  async updateUserProfile(data: { name?: string; companyId?: string }) {
    return this.put('/users/profile', data);
  }

  // ===== Company Settings =====
  async getCompanySettings(params?: { companyId?: string }) {
    const qp = new URLSearchParams();
    if (params?.companyId) qp.append('companyId', params.companyId);
    const q = qp.toString();
    return this.get(`/settings/company${q ? `?${q}` : ''}`);
  }

  async updateCompanySettings(data: { companyName?: string; companyLogo?: string | null; companyContactNumber?: string | null; companyEmail?: string | null; companyId?: string }) {
    return this.put(`/settings/company`, data);
  }

  async listCompanyAddresses(params?: { companyId?: string }) {
    const qp = new URLSearchParams();
    if (params?.companyId) qp.append('companyId', params.companyId);
    const q = qp.toString();
    return this.get(`/settings/company/addresses${q ? `?${q}` : ''}`);
  }

  async upsertCompanyAddress(data: { id?: string; type: 'delivery' | 'correspondence' | 'billing' | 'other'; contactName: string; detailedAddress: string; country: string; phone?: string | null; isDefault: boolean; companyId?: string }) {
    return this.post(`/settings/company/addresses`, data);
  }

  async deleteCompanyAddress(id: string, params?: { companyId?: string }) {
    const qp = new URLSearchParams();
    if (params?.companyId) qp.append('companyId', params.companyId);
    const q = qp.toString();
    return this.delete(`/settings/company/addresses/${encodeURIComponent(id)}${q ? `?${q}` : ''}`);
  }

  // 刷新 token - 使用與 tryRefreshTokensOnce 相同的協調機制，避免並發刷新導致的競態條件
  async refreshTokens(payload: { refreshToken: string }) {
    // 如果已經有刷新請求在進行，等待它完成
    if (this.isRefreshing && this.refreshPromise) {
      await this.refreshPromise;
      return { success: !!localStorage.getItem('authToken') };
    }

    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      // 直接使用無授權請求，避免帶入過期 Bearer 導致 401
      const resp = await fetch(`${this.baseURL}/users/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!resp.ok) {
        // 僅在 400/401 視為憑證問題並清除 token（使用寬限策略）
        if (resp.status === 400 || resp.status === 401) {
          if (this.shouldClearTokensOnRefreshAuthError()) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('refreshToken');
          }
        }
        const text = await resp.text().catch(() => '');
        throw new Error(text || `HTTP error! status: ${resp.status}`);
      }
      const data = await resp.json().catch(() => null);
      if (data?.success && data?.data) {
        const { token: newAccessToken, refreshToken: newRefreshToken } = data.data;
        if (newAccessToken) localStorage.setItem('authToken', newAccessToken);
        if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);
      }
      return data;
    })();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  // 登出（撤銷 refresh token）
  async logout(payload: { refreshToken?: string; revokeAllForUser?: boolean }) {
    return this.post('/users/logout', payload);
  }

  // 用戶註冊
  async register(userData: {
    email: string;
    password: string;
    name: string;
    role?: string;
    companyId?: string;
  }) {
    return this.post('/users/register', userData);
  }

  // 重置密碼 (管理員功能)
  async resetPassword(data: { email: string; newPassword: string }) {
    return this.post('/users/reset-password', data);
  }

  // 獲取解密後的密碼 (管理員功能)
  async getDecryptedPassword(userId: string) {
    return this.get(`/users/${userId}/decrypted-password`);
  }

  // ===== PRIORITY 3: NEW CLEAN APIs =====

  // Customers endpoint
  async getCustomers(params?: { page?: number; limit?: number; companyId?: string; search?: string; isActive?: boolean }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.companyId) qp.append('companyId', params.companyId);
    if (params?.search) qp.append('search', params.search);
    if (params?.isActive !== undefined) qp.append('isActive', String(params.isActive));
    const q = qp.toString();
    return this.get(`/customers${q ? `?${q}` : ''}`);
  }

  // ===== Picking (Batch Picking Lists) =====
  async getPickingLists(params?: { page?: number; limit?: number; search?: string; status?: string; priority?: string; dateFrom?: string; dateTo?: string; sortField?: string; sortOrder?: 'asc' | 'desc' }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.search) qp.append('search', params.search);
    if (params?.status) qp.append('status', params.status);
    if (params?.priority) qp.append('priority', params.priority);
    if (params?.dateFrom) qp.append('dateFrom', params.dateFrom);
    if (params?.dateTo) qp.append('dateTo', params.dateTo);
    if (params?.sortField) qp.append('sortField', params.sortField);
    if (params?.sortOrder) qp.append('sortOrder', params.sortOrder);
    const q = qp.toString();
    return this.get(`/picking${q ? `?${q}` : ''}`);
  }

  async createPickingList(data: any) {
    return this.post('/picking', data);
  }

  // 依批次單號/ID 取得其包含的出庫訂單
  async getPickingOrders(batchIdOrNumber: string) {
    return this.get(`/picking/${encodeURIComponent(batchIdOrNumber)}/orders`);
  }

  async getPickingCreateTableSQL(): Promise<string> {
    const url = `${this.baseURL}/picking/schema-sql`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`);
    return await resp.text();
  }

  async getPickingPrintData(idOrNumber: string): Promise<any> {
    return this.get(`/picking/${encodeURIComponent(idOrNumber)}/print-data`);
  }

  // ===== Picking Scan Progress (方案B) =====
  async getPickingScanItems(idOrNumber: string) {
    return this.get(`/picking/${encodeURIComponent(idOrNumber)}/scan-items`);
  }

  async upsertPickingScanItems(idOrNumber: string, items: Array<{ orderNumber?: string | null; sku: string; barcode?: string | null; totalQty?: number; pickedQty?: number; confirmedQty?: number }>) {
    return this.post(`/picking/${encodeURIComponent(idOrNumber)}/scan-items/upsert`, { items });
  }

  // New: 依運單號查詢出庫產品（不依賴批次）
  async getPickingItemsByWaybill(waybillNumber: string) {
    return this.get(`/picking/by-waybill/${encodeURIComponent(waybillNumber)}`);
  }

  async getPickingInsertSQL(): Promise<string> {
    const url = `${this.baseURL}/picking/insert-sql`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`);
    return await resp.text();
  }

  async getCustomersV2(params?: {
    page?: number;
    limit?: number;
    companyId?: string;
    search?: string;
    isActive?: boolean;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.companyId) queryParams.append('companyId', params.companyId);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

    const query = queryParams.toString();
    return this.get(`/customers${query ? `?${query}` : ''}`);
  }

  async getCustomerByIdV2(id: string, companyId?: string) {
    const params = companyId ? `?companyId=${companyId}` : '';
    return this.get(`/customers/${id}${params}`);
  }

  async createCustomerV2(customerData: any) {
    return this.post('/customers', customerData);
  }

  async updateCustomerV2(id: string, customerData: any) {
    return this.put(`/customers/${id}`, customerData);
  }

  async deleteCustomerV2(id: string, companyId?: string) {
    const params = companyId ? `?companyId=${companyId}` : '';
    return this.delete(`/customers/${id}${params}`);
  }

  async getCustomerStatsV2(companyId?: string) {
    const params = companyId ? `?companyId=${companyId}` : '';
    return this.get(`/customers/stats${params}`);
  }

  // Product Management API v2 (Priority 3)
  async getProductsV2(params?: {
    page?: number;
    limit?: number;
    companyId?: string;
    search?: string;
    sku?: string;
    barcode?: string;
    sortField?: string;
    sortOrder?: 'asc' | 'desc';
    customerName?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.companyId) queryParams.append('companyId', params.companyId);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sku) queryParams.append('sku', params.sku);
    if (params?.barcode) queryParams.append('barcode', params.barcode);
    if (params?.sortField) queryParams.append('sortField', params.sortField);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.customerName) queryParams.append('customerName', params.customerName);

    const query = queryParams.toString();
    return this.get(`/products-v2${query ? `?${query}` : ''}`);
  }

  async getProductByIdV2(id: string, companyId?: string) {
    const params = companyId ? `?companyId=${companyId}` : '';
    return this.get(`/products-v2/${id}${params}`);
  }

  // === Batch strategies (for 批次控制設定/預設訂單分配策略) ===
  async getBatchStrategies() {
    return this.get(`/products-v2/meta/batch-strategies`);
  }
  async seedBatchStrategies() {
    return this.post(`/products-v2/meta/batch-strategies/seed`, {});
  }

  async createProductV2(productData: any) {
    return this.post('/products-v2', productData);
  }

  async bulkCreateProducts(products: any[], bundleComponents: any[] = []) {
    return this.post('/products-v2/bulk', { products, bundleComponents });
  }

  async resolveProductSkus(skus: string[]) {
    return this.post('/products-v2/skus/resolve', { skus });
  }

  async updateProductV2(id: string, productData: any) {
    return this.put(`/products-v2/${id}`, productData);
  }

  async getAvailableStoreCodes() {
    return this.get('/products-v2/store-codes/available');
  }

  async getEligibleAlternateSkus(search: string, excludeProductId?: string) {
    const qp = new URLSearchParams();
    qp.append('search', search);
    if (excludeProductId) qp.append('excludeProductId', excludeProductId);
    return this.get(`/products-v2/alternate-skus/eligible?${qp.toString()}`);
  }

  async deleteProductV2(id: string, opts?: { companyId?: string; force?: boolean }) {
    const qp = new URLSearchParams();
    if (opts?.companyId) qp.append('companyId', opts.companyId);
    if (opts?.force) qp.append('force', 'true');
    const q = qp.toString();
    return this.delete(`/products-v2/${encodeURIComponent(id)}${q ? `?${q}` : ''}`);
  }

  async getLowStockItemsV2(companyId?: string) {
    const params = companyId ? `?companyId=${companyId}` : '';
    return this.get(`/products-v2/low-stock${params}`);
  }

  // Sales Management API v2 (Priority 3)
  async getSalesStatsV2(companyId?: string) {
    const params = companyId ? `?companyId=${companyId}` : '';
    return this.get(`/sales-v2/stats${params}`);
  }

  async createSalesOrderV2(orderData: any) {
    return this.post('/sales-v2/orders', orderData);
  }

  async updateSalesOrderV2(id: string, orderData: any) {
    return this.put(`/sales-v2/orders/${encodeURIComponent(id)}`, orderData);
  }

  async getSalesOrdersV2(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.append('dateTo', params.dateTo);

    const query = queryParams.toString();
    return this.get(`/sales-v2/orders${query ? `?${query}` : ''}`);
  }

  async getSalesOrderByIdV2(id: string, companyId?: string) {
    const params = companyId ? `?companyId=${companyId}` : '';
    return this.get(`/sales-v2/orders/${encodeURIComponent(id)}${params}`);
  }

  async updateSalesOrderStatusV2(id: string, status: string, companyId?: string) {
    const data = companyId ? { status, companyId } : { status };
    return this.patch(`/sales-v2/orders/${id}/status`, data);
  }

  async deleteSalesOrderV2(id: string) {
    return this.delete(`/sales-v2/orders/${encodeURIComponent(id)}`);
  }

  async createInvoiceForOrderV2(id: string, payload: { userId?: string; companyId?: string; dueDate?: string }) {
    return this.post(`/sales-v2/orders/${encodeURIComponent(id)}/invoice`, payload);
  }

  // ===== Invoice Management =====
  async getInvoices(params?: {
    page?: number;
    limit?: number;
    companyId?: string;
    status?: string;
    customerId?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
  }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.companyId) qp.append('companyId', params.companyId);
    if (params?.status) qp.append('status', params.status);
    if (params?.customerId) qp.append('customerId', params.customerId);
    if (params?.search) qp.append('search', params.search);
    if (params?.dateFrom) qp.append('dateFrom', params.dateFrom);
    if (params?.dateTo) qp.append('dateTo', params.dateTo);
    const q = qp.toString();
    return this.get(`/accounting/invoices${q ? `?${q}` : ''}`);
  }

  async getInvoiceById(id: string) {
    return this.get(`/accounting/invoices/${encodeURIComponent(id)}`);
  }

  async createInvoice(data: any) {
    return this.post('/accounting/invoices', data);
  }

  async updateInvoice(id: string, data: any) {
    return this.put(`/accounting/invoices/${encodeURIComponent(id)}`, data);
  }

  async deleteInvoice(id: string) {
    return this.delete(`/accounting/invoices/${encodeURIComponent(id)}`);
  }

  async updateInvoiceStatus(id: string, status: string) {
    return this.patch(`/accounting/invoices/${encodeURIComponent(id)}/status`, { status });
  }

  async createQuotationV2(quotationData: any) {
    return this.post('/sales-v2/quotations', quotationData);
  }

  async getQuotationsV2(params?: {
    page?: number;
    limit?: number;
    companyId?: string;
    status?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.companyId) queryParams.append('companyId', params.companyId);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.append('dateTo', params.dateTo);

    const query = queryParams.toString();
    return this.get(`/sales-v2/quotations${query ? `?${query}` : ''}`);
  }

  async updateQuotationStatusV2(id: string, status: string) {
    return this.patch(`/sales-v2/quotations/${encodeURIComponent(id)}/status`, { status });
  }

  async getQuotationByIdV2(id: string) {
    return this.get(`/sales-v2/quotations/${encodeURIComponent(id)}`);
  }

  async deleteQuotationV2(id: string) {
    return this.delete(`/sales-v2/quotations/${encodeURIComponent(id)}`);
  }

  // Quotation create (helper for Create Quotation page)
  async createQuotation(data: any) {
    return this.post('/sales-v2/quotations', data);
  }

  // Priority 3 Status Monitor
  async getPriority3Status() {
    return this.get('/priority-3/status');
  }

  // ===== Return Management APIs =====
  async getReturns(params?: { page?: number; limit?: number; search?: string; status?: string; dateFrom?: string; dateTo?: string; }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.search) qp.append('search', params.search);
    if (params?.status) qp.append('status', params.status);
    if (params?.dateFrom) qp.append('dateFrom', params.dateFrom);
    if (params?.dateTo) qp.append('dateTo', params.dateTo);
    const query = qp.toString();
    return this.get(`/returns${query ? `?${query}` : ''}`);
  }

  async getReturnById(id: string) {
    return this.get(`/returns/${encodeURIComponent(id)}`);
  }

  async processReturn(id: string, data: { status?: string; merchantConfirmation?: string; handlingMethod?: string; refundMethod?: string; processedBy?: string; processedAt?: string; handlingNotes?: string; }) {
    return this.put(`/returns/${encodeURIComponent(id)}/process`, data);
  }

  async createReturn(data: any) {
    return this.post('/returns', data);
  }

  async getNextReturnNumber() {
    return this.get('/returns/next-number');
  }

  // Outbound list
  // 注意：該方法已在下方「Outbond orders for Picking Management」區塊實作，避免重複定義

  // ===== EXISTING APIs (Legacy Support) =====

  // Note: Users API methods (getUsers, getUserById, createUser, updateUser, deleteUser)
  // are defined above in the Users section to avoid duplicates

  async getUserPermissions(id: string) {
    return this.get(`/users/${id}/permissions`);
  }

  // Roles & Permissions API
  async getRoles() {
    return this.get('/settings/roles');
  }

  async getPermissions() {
    return this.get('/settings/permissions');
  }

  async updateUserRoles(userId: string, data: { roleIds: string[] }) {
    return this.put(`/users/${userId}`, data);
  }

  // Companies API
  async getCompanies(params?: { page?: number; limit?: number; search?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);

    const query = queryParams.toString();
    return this.get(`/companies${query ? `?${query}` : ''}`);
  }

  async getCompanyById(id: string) {
    return this.get(`/companies/${id}`);
  }

  async createCompany(companyData: any) {
    return this.post('/companies', companyData);
  }

  async updateCompany(id: string, companyData: any) {
    return this.put(`/companies/${id}`, companyData);
  }

  async deleteCompany(id: string) {
    return this.delete(`/companies/${id}`);
  }

  async getCompanyStats(id: string) {
    return this.get(`/companies/${id}/stats`);
  }

  // Products API (Legacy)
  async getProducts(params?: {
    page?: number;
    limit?: number;
    companyId?: string;
    categoryId?: string;
    search?: string;
    lowStock?: boolean;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.companyId) queryParams.append('companyId', params.companyId);
    if (params?.categoryId) queryParams.append('categoryId', params.categoryId);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.lowStock) queryParams.append('lowStock', 'true');

    const query = queryParams.toString();
    return this.get(`/products${query ? `?${query}` : ''}`);
  }

  async getProductById(id: string) {
    return this.get(`/products/${id}`);
  }

  async createProduct(productData: any) {
    return this.post('/products', productData);
  }

  async updateProduct(id: string, productData: any) {
    return this.put(`/products/${id}`, productData);
  }

  async deleteProduct(id: string) {
    return this.delete(`/products/${id}`);
  }

  async getLowStockItems(params?: { companyId?: string; warehouseId?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.companyId) queryParams.append('companyId', params.companyId);
    if (params?.warehouseId) queryParams.append('warehouseId', params.warehouseId);

    const query = queryParams.toString();
    return this.get(`/products/low-stock${query ? `?${query}` : ''}`);
  }

  async createBOM(bomData: any) {
    return this.post('/products/bom', bomData);
  }

  // ===== ENHANCED DASHBOARD APIs (Priority 4) =====

  // Dashboard Statistics - Enhanced with Priority 3 Integration
  async getDashboardStats(companyId?: string) {
    try {
      // Try Priority 3 APIs first for real-time data
      const [customerStats, salesStats, productStats] = await Promise.allSettled([
        this.getCustomerStatsV2(companyId),
        this.getSalesStatsV2(companyId),
        this.getLowStockItemsV2(companyId)
      ]);

      // Combine results with fallback to legacy endpoints
      const dashboardData = {
        timestamp: new Date().toISOString(),
        dataSource: 'priority-3-apis',
        salesStats: salesStats.status === 'fulfilled' ? salesStats.value : null,
        crmStats: customerStats.status === 'fulfilled' ? customerStats.value : null,
        inventoryStats: productStats.status === 'fulfilled' ? productStats.value : null,
        priority3Status: await this.getPriority3Status().catch(() => null)
      };

      return { success: true, data: dashboardData };
    } catch (error) {
      // Fallback to legacy dashboard endpoint
      console.warn('Priority 3 APIs unavailable, falling back to legacy dashboard:', error);
      const params = companyId ? `?companyId=${companyId}` : '';
      return this.get(`/dashboard/stats${params}`);
    }
  }

  // Sales API (Legacy - will be replaced by Priority 3)
  async getSalesOrders(params?: { page?: number; limit?: number; companyId?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.companyId) queryParams.append('companyId', params.companyId);

    const query = queryParams.toString();
    return this.get(`/sales/orders${query ? `?${query}` : ''}`);
  }

  async getSalesStats(companyId?: string) {
    // Try Priority 3 API first, fallback to legacy
    try {
      return await this.getSalesStatsV2(companyId);
    } catch (error) {
      console.warn('Priority 3 sales API unavailable, falling back to legacy:', error);
      const params = companyId ? `?companyId=${companyId}` : '';
      return this.get(`/sales/stats${params}`);
    }
  }

  // CRM API (Legacy - will be replaced by Priority 3)
  async getCRMStats(companyId?: string) {
    // Try Priority 3 API first, fallback to legacy
    try {
      return await this.getCustomerStatsV2(companyId);
    } catch (error) {
      console.warn('Priority 3 customer API unavailable, falling back to legacy:', error);
      const params = companyId ? `?companyId=${companyId}` : '';
      return this.get(`/crm/stats${params}`);
    }
  }

  // Inventory API (Legacy - will be replaced by Priority 3)
  async getInventoryStats(companyId?: string, warehouseId?: string) {
    // Try Priority 3 API first, fallback to legacy
    try {
      const lowStockItems: any[] = await this.getLowStockItemsV2(companyId) as any[];
      return {
        lowStockItems: lowStockItems.length || 0,
        lowStockDetails: lowStockItems,
        totalProducts: 0, // To be implemented
        stockValue: 0, // To be implemented
        warehouseCount: 1 // Default
      };
    } catch (error) {
      console.warn('Priority 3 inventory API unavailable, falling back to legacy:', error);
      const queryParams = new URLSearchParams();
      if (companyId) queryParams.append('companyId', companyId);
      if (warehouseId) queryParams.append('warehouseId', warehouseId);

      const query = queryParams.toString();
      return this.get(`/inventory/stats${query ? `?${query}` : ''}`);
    }
  }

  // Warehouse API (will be implemented with backend)
  async getWarehouses(params?: { page?: number; limit?: number; search?: string; sortField?: string; sortOrder?: 'asc' | 'desc'; companyId?: string }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.search) qp.append('search', params.search);
    if (params?.sortField) qp.append('sortField', params.sortField);
    if (params?.sortOrder) qp.append('sortOrder', params.sortOrder);
    if (params?.companyId) qp.append('companyId', params.companyId);
    const q = qp.toString();
    return this.get(`/warehouses${q ? `?${q}` : ''}`);
  }

  async deleteWarehouse(id: string) {
    return this.delete(`/warehouses/${encodeURIComponent(id)}`);
  }

  async updateWarehouse(
      id: string,
      data: {
        name?: string;
        code?: string;
        type?: string;
        capacityTotal?: number;
        capacityUnit?: string;
        capacityUsed?: number;
        capacityReserved?: number;
        temperatureRequirement?: string;
        location?: string;
        isActive?: boolean;
      }
  ) {
    return this.put(`/warehouses/${encodeURIComponent(id)}`, data);
  }

  async createWarehouse(data: {
    name: string;
    code?: string;
    type?: string;
    capacityTotal?: number;
    capacityUnit?: string;
    capacityUsed?: number;
    capacityReserved?: number;
    temperatureRequirement?: string;
    location?: string;
    companyId?: string;
  }) {
    return this.post('/warehouses', data);
  }

  async getCodingRules(params?: { page?: number; limit?: number; search?: string; sortField?: string; sortOrder?: 'asc' | 'desc' }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.search) qp.append('search', params.search);
    if (params?.sortField) qp.append('sortField', params.sortField);
    if (params?.sortOrder) qp.append('sortOrder', params.sortOrder);
    const q = qp.toString();
    return this.get(`/coding-rules${q ? `?${q}` : ''}`);
  }

  async deleteCodingRule(id: string) {
    return this.delete(`/coding-rules/${encodeURIComponent(id)}`);
  }

  async createCodingRule(payload: any) {
    return this.post('/coding-rules', payload);
  }

  async getCodingRulePreview(id: string) {
    return this.get(`/coding-rules/${encodeURIComponent(id)}/preview`);
  }

  async updateCodingRule(id: string, payload: any) {
    return this.put(`/coding-rules/${encodeURIComponent(id)}`, payload);
  }

  // ===== Warehouse Locations =====
  async getWarehouseLocations(
      warehouseId: string,
      params?: { flat?: boolean; level?: number; parentId?: string; search?: string; page?: number; pageSize?: number; companyId?: string }
  ) {
    const qp = new URLSearchParams();
    if (params?.flat !== undefined) qp.append('flat', String(params.flat));
    if (params?.level !== undefined) qp.append('level', String(params.level));
    if (params?.parentId !== undefined) qp.append('parentId', params.parentId);
    if (params?.search !== undefined) qp.append('search', params.search);
    if (params?.page !== undefined) qp.append('page', String(params.page));
    if (params?.pageSize !== undefined) qp.append('pageSize', String(params.pageSize));
    if (params?.companyId !== undefined) qp.append('companyId', params.companyId);
    const q = qp.toString();
    return this.get(`/warehouses/${encodeURIComponent(warehouseId)}/locations${q ? `?${q}` : ''}`);
  }

  async createLocation(warehouseId: string, data: any) {
    return this.post(`/warehouses/${encodeURIComponent(warehouseId)}/locations`, data);
  }

  async updateLocation(id: string, data: any) {
    return this.put(`/locations/${encodeURIComponent(id)}`, data);
  }

  async deleteLocation(id: string, force?: boolean) {
    return this.delete(`/warehouse-management/locations/${encodeURIComponent(id)}${force ? '?force=true' : ''}`);
  }

  // Internal Transfer APIs
  async getInternalTransfers(params?: { page?: number; limit?: number; status?: string; warehouse?: string; search?: string; companyId?: string; }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.status) qp.append('status', params.status);
    if (params?.warehouse) qp.append('warehouse', params.warehouse);
    if (params?.search) qp.append('search', params.search);
    if (params?.companyId) qp.append('companyId', params.companyId);
    const query = qp.toString();
    return this.get(`/inventory/transfers${query ? `?${query}` : ''}`);
  }

  async createInternalTransfer(data: any) {
    return this.post('/inventory/transfers', data);
  }

  async updateInternalTransfer(id: string, data: any) {
    return this.put(`/inventory/transfers/${encodeURIComponent(id)}`, data);
  }

  async updateInternalTransferStatus(id: string, status: string) {
    return this.patch(`/inventory/transfers/${encodeURIComponent(id)}/status`, { status });
  }

  async deleteInternalTransfer(id: string) {
    return this.delete(`/inventory/transfers/${encodeURIComponent(id)}`);
  }

  // BOM Management
  async getBOMs(productId?: string) {
    const params = productId ? `?productId=${productId}` : '';
    return this.get(`/products/bom${params}`);
  }

  // Stock Management API
  async getStockList(params?: { page?: number; limit?: number; companyId?: string; warehouseId?: string; search?: string; status?: string; sortField?: string; sortOrder?: 'asc' | 'desc'; customerName?: string; storeCode?: string }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.companyId) qp.append('companyId', params.companyId);
    if (params?.warehouseId) qp.append('warehouseId', params.warehouseId);
    if (params?.search) qp.append('search', params.search);
    if (params?.status) qp.append('status', params.status);
    if (params?.sortField) qp.append('sortField', params.sortField);
    if (params?.sortOrder) qp.append('sortOrder', params.sortOrder);
    if (params?.customerName) qp.append('customerName', params.customerName);
    if (params?.storeCode) qp.append('storeCode', params.storeCode);
    const query = qp.toString();
    return this.get(`/stock-management${query ? `?${query}` : ''}`);
  }

  // Get single stock item by id
  async getStockItem(id: string) {
    return this.get(`/stock-management/${encodeURIComponent(id)}`);
  }

  // Get inventory by product and warehouse (for internal transfer location stock display)
  async getInventoryByProductWarehouse(params: { productId?: string; warehouseId?: string; companyId?: string }) {
    const qp = new URLSearchParams();
    if (params.productId) qp.append('productId', params.productId);
    if (params.warehouseId) qp.append('warehouseId', params.warehouseId);
    if (params.companyId) qp.append('companyId', params.companyId);
    const query = qp.toString();
    return this.get(`/wms/inventory/states${query ? `?${query}` : ''}`);
  }

  // Get stock by location for a product (from inbound_order_items)
  // Returns per-location stock including "未分配庫位" for warehouse-level stock
  async getStockByLocation(params: { productId?: string; productSku?: string; warehouseId?: string }) {
    const qp = new URLSearchParams();
    if (params.productId) qp.append('productId', params.productId);
    if (params.productSku) qp.append('productSku', params.productSku);
    if (params.warehouseId) qp.append('warehouseId', params.warehouseId);
    const query = qp.toString();
    return this.get(`/wms/inventory/stock-by-location${query ? `?${query}` : ''}`);
  }

  // Get related orders with pagination
  async getRelatedOrders(productId: string, params?: { page?: number; limit?: number; type?: 'all' | 'outbound' | 'inbound' | 'adjustment' }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.type) qp.append('type', params.type);
    const q = qp.toString();
    return this.get(`/stock-management/${encodeURIComponent(productId)}/related-orders${q ? `?${q}` : ''}`);
  }

  // 超售欠貨補發：建立補發單並解除欠貨
  async resolveOversellDebt(debtId: string) {
    return this.post(`/stock-management/debt/${encodeURIComponent(debtId)}/resolve`, {});
  }

  // Update stock item (e.g., adjust quantity or metadata)
  async updateStockItem(id: string, data: any) {
    return this.patch(`/stock-management/${encodeURIComponent(id)}`, data);
  }

  // Get stock adjustments list (for audit tab)
  async getStockAdjustments(params?: { page?: number; limit?: number; status?: 'approved' | 'pending' | 'rejected'; sku?: string }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.status) qp.append('status', params.status);
    if (params?.sku) qp.append('sku', params.sku);
    const q = qp.toString();
    return this.get(`/stock-management/adjustments/list${q ? `?${q}` : ''}`);
  }

  // Approve/Reject single stock adjustment (by movement id)
  async processStockAdjustment(id: string, payload: { action: 'approve' | 'reject'; reason?: string; reviewedBy?: string }) {
    return this.post(`/stock-management/adjustments/${encodeURIComponent(id)}/process`, payload);
  }

  // Batch approve/reject adjustments
  async batchProcessStockAdjustments(payload: { action: 'approve' | 'reject'; ids: string[]; reason?: string; reviewedBy?: string }) {
    return this.post(`/stock-management/adjustments/batch-process`, payload);
  }

  // Export all stock data with location, lot, expiry (not paginated)
  async exportStockData(params?: { search?: string; customerName?: string; storeCode?: string; asOfDate?: string; asOfMoment?: string; includeAggregateRemainder?: boolean }) {
    const qp = new URLSearchParams();
    if (params?.search) qp.append('search', params.search);
    if (params?.customerName) qp.append('customerName', params.customerName);
    if (params?.storeCode) qp.append('storeCode', params.storeCode);
    if (params?.asOfDate) qp.append('asOfDate', params.asOfDate);
    // Without this line the time is dropped here and the export silently
    // falls back to the nightly snapshot — this list is an allow-list.
    if (params?.asOfMoment) qp.append('asOfMoment', params.asOfMoment);
    // Stock-take lists only: adds a row per SKU for stock that exists in the
    // aggregate but in no lot row, which a lot-built count list would miss.
    if (params?.includeAggregateRemainder) qp.append('includeAggregateRemainder', 'true');
    const query = qp.toString();
    return this.get(`/stock-management/export${query ? `?${query}` : ''}`);
  }

  // Get customers with inventory for filter dropdown
  async getCustomersWithInventory() {
    return this.get('/stock-management/customers-with-inventory');
  }

  // Get store codes with inventory for filter dropdown
  async getStoreCodesWithInventory() {
    return this.get('/stock-management/store-codes-with-inventory');
  }

  // 一鍵清零 - 批量重置庫存為零
  async bulkResetInventory(skus: string[]): Promise<{
    success: boolean;
    message?: string;
    data?: {
      skusProcessed: number;
      deletedInvTx: number;
      deletedMovements: number;
      deletedBatches: number;
      resetInventory: number;
      resetInboundStock: number;
    };
    error?: string;
  }> {
    return this.post('/stock-management/bulk-reset', { skus });
  }

  // 庫存調整申請 - 按位置調整 (新功能)
  // Get stock locations for a product (庫存調整申請 - 查看位置庫存)
  async getStockLocations(productId: string): Promise<{
    success: boolean;
    data: {
      productId: string;
      sku: string;
      productName: string;
      locations: Array<{
        inboundItemId: string;
        locationId: string;
        locationCode: string;
        locationName: string;
        warehouseId: string;
        warehouseName: string;
        currentStock: number;
        lotNumber: string | null;
        expiryDate: string | null;
        inboundId: number;
      }>;
      totalLocationStock: number;
      inventoryTotals: Array<{
        warehouseId: string;
        warehouseName: string;
        totalQuantity: number;
        availableQuantity: number;
        reservedQuantity: number;
        shippingQuantity: number;
      }>;
    };
  }> {
    return this.get(`/stock-management/${encodeURIComponent(productId)}/locations`);
  }

  // Submit location-based stock adjustment (庫存調整申請 - 提交位置調整)
  async submitLocationAdjustment(productId: string, data: {
    locationAdjustments: Array<{
      inboundItemId: string;
      adjustmentQuantity: number;
    }>;
    reasonCode: string;
    notes?: string;
  }): Promise<{
    success: boolean;
    data: {
      adjustmentReference: string;
      productId: string;
      sku: string;
      productName: string;
      results: Array<{
        inboundItemId: string;
        success: boolean;
        previousStock?: number;
        adjustmentQuantity?: number;
        newStock?: number;
        locationId?: string;
        lotNumber?: string;
        error?: string;
      }>;
      updatedInventory: Array<{
        warehouseId: string;
        warehouseName: string;
        totalQuantity: number;
        availableQuantity: number;
        reservedQuantity: number;
        shippingQuantity: number;
      }>;
    };
  }> {
    return this.post(`/stock-management/${encodeURIComponent(productId)}/location-adjustment`, data);
  }

  // Inbound Orders - Overview
  async getInboundOrders(params?: { page?: number; limit?: number; search?: string; status?: string; customerId?: string; receiptDateStart?: string; receiptDateEnd?: string }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.search) qp.append('search', params.search);
    if (params?.status) qp.append('status', params.status);
    if (params?.customerId) qp.append('customerId', params.customerId);
    if (params?.receiptDateStart) qp.append('receiptDateStart', params.receiptDateStart);
    if (params?.receiptDateEnd) qp.append('receiptDateEnd', params.receiptDateEnd);
    const q = qp.toString();
    return this.get(`/inbound/orders${q ? `?${q}` : ''}`);
  }

  async getInboundOrderById(id: string) {
    return this.get(`/inbound/orders/${encodeURIComponent(id)}`);
  }

  async createInboundOrder(data: any) {
    return this.post('/inbound/orders', data);
  }

  async bulkCreateInboundOrders(orders: any[]) {
    return this.post('/inbound/orders/bulk', { orders });
  }

  async updateInboundOrder(id: string, data: any) {
    return this.patch(`/inbound/orders/${encodeURIComponent(id)}`, data);
  }

  async getInboundOrderItems(orderId: string) {
    return this.get(`/inbound/orders/${encodeURIComponent(orderId)}/items`);
  }

  async upsertInboundOrderItem(orderId: string, item: any) {
    return this.post(`/inbound/orders/${encodeURIComponent(orderId)}/items`, item);
  }

  // === Product lookups for auto-complete ===
  async getProductLookups(params?: { search?: string; limit?: number; companyId?: string }) {
    const qp = new URLSearchParams();
    if (params?.search) qp.append('search', params.search);
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.companyId) qp.append('companyId', params.companyId);
    const q = qp.toString();
    return this.get(`/products/lookup${q ? `?${q}` : ''}`);
  }

  async getProductByBarcode(barcode: string) {
    const qp = new URLSearchParams();
    qp.append('barcode', barcode);
    return this.get(`/products-v2/lookup/by-barcode?${qp.toString()}`);
  }

  async deleteInboundOrderItem(orderId: string, itemId: string) {
    return this.delete(`/inbound/orders/${encodeURIComponent(orderId)}/items/${encodeURIComponent(itemId)}`);
  }

  // Cancel/delete an inbound order (only if status is 'new' and no stock received)
  async cancelInboundOrder(orderId: string) {
    return this.delete(`/inbound/orders/${encodeURIComponent(orderId)}`);
  }

  // Temperature profiles
  async getTemperatureProfiles() {
    return this.get('/inbound/temperatures');
  }

  // Inbound vouchers (reports)
  async getInboundVouchers(params?: { page?: number; limit?: number; search?: string; status?: string; startDate?: string; endDate?: string }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.search) qp.append('search', params.search);
    if (params?.status) qp.append('status', params.status);
    if (params?.startDate) qp.append('startDate', params.startDate);
    if (params?.endDate) qp.append('endDate', params.endDate);
    const q = qp.toString();
    return this.get(`/inbound/vouchers${q ? `?${q}` : ''}`);
  }

  async getInboundVoucherById(id: string) {
    return this.get(`/inbound/vouchers/${encodeURIComponent(id)}`);
  }

  // Outbound orders for Picking Management
  async getOutboundOrders(params?: { page?: number; limit?: number; search?: string; status?: string; warehouseId?: string; customerId?: string; dateFrom?: string; dateTo?: string; sortField?: string; sortOrder?: 'asc' | 'desc'; salesChannel?: string; pickUpDateStart?: string; pickUpDateEnd?: string; createdDateStart?: string; createdDateEnd?: string; storeId?: string; hasStockIssues?: string; runTagId?: string; itemSku?: string }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.search) qp.append('search', params.search);
    if (params?.status) qp.append('status', params.status);
    if (params?.warehouseId) qp.append('warehouseId', params.warehouseId);
    if (params?.customerId) qp.append('customerId', params.customerId);
    if (params?.dateFrom) qp.append('dateFrom', params.dateFrom);
    if (params?.dateTo) qp.append('dateTo', params.dateTo);
    if (params?.sortField) qp.append('sortField', params.sortField);
    if (params?.sortOrder) qp.append('sortOrder', params.sortOrder);
    if (params?.salesChannel) qp.append('salesChannel', params.salesChannel);
    if (params?.pickUpDateStart) qp.append('pickUpDateStart', params.pickUpDateStart);
    if (params?.pickUpDateEnd) qp.append('pickUpDateEnd', params.pickUpDateEnd);
    if (params?.createdDateStart) qp.append('createdDateStart', params.createdDateStart);
    if (params?.createdDateEnd) qp.append('createdDateEnd', params.createdDateEnd);
    if (params?.storeId) qp.append('storeId', params.storeId);
    if (params?.hasStockIssues) qp.append('hasStockIssues', params.hasStockIssues);
    if (params?.runTagId) qp.append('runTagId', params.runTagId);
    if (params?.itemSku) qp.append('itemSku', params.itemSku);
    const q = qp.toString();
    return this.get(`/outbound${q ? `?${q}` : ''}`);
  }

  // 導出出庫訂單（CSV），根據相同篩選條件
  async exportOutboundOrdersCSV(params?: { search?: string; status?: string; warehouseId?: string; customerId?: string; dateFrom?: string; dateTo?: string; sortField?: string; sortOrder?: 'asc' | 'desc'; salesChannel?: string; pickUpDateStart?: string; pickUpDateEnd?: string; createdDateStart?: string; createdDateEnd?: string; storeId?: string; hasStockIssues?: string; runTagId?: string }) {
    const qp = new URLSearchParams();
    if (params?.search) qp.append('search', params.search);
    if (params?.status) qp.append('status', params.status);
    if (params?.warehouseId) qp.append('warehouseId', params.warehouseId);
    if (params?.customerId) qp.append('customerId', params.customerId);
    if (params?.dateFrom) qp.append('dateFrom', params.dateFrom);
    if (params?.dateTo) qp.append('dateTo', params.dateTo);
    if (params?.sortField) qp.append('sortField', params.sortField);
    if (params?.sortOrder) qp.append('sortOrder', params.sortOrder);
    if (params?.salesChannel) qp.append('salesChannel', params.salesChannel);
    if (params?.pickUpDateStart) qp.append('pickUpDateStart', params.pickUpDateStart);
    if (params?.pickUpDateEnd) qp.append('pickUpDateEnd', params.pickUpDateEnd);
    if (params?.createdDateStart) qp.append('createdDateStart', params.createdDateStart);
    if (params?.createdDateEnd) qp.append('createdDateEnd', params.createdDateEnd);
    if (params?.storeId) qp.append('storeId', params.storeId);
    if (params?.hasStockIssues) qp.append('hasStockIssues', params.hasStockIssues);
    if (params?.runTagId) qp.append('runTagId', params.runTagId);

    const token = localStorage.getItem('authToken');
    const url = `${this.baseURL}/outbound/export${qp.toString() ? `?${qp.toString()}` : ''}`;
    const resp = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        'Accept': 'text/csv'
      }
    });
    if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`);
    const blob = await resp.blob();
    // 解析檔名
    const disposition = resp.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const fileName = match ? decodeURIComponent(match[1] || match[2]) : `outbound_export_${new Date().toISOString().slice(0,10)}.csv`;
    return { blob, fileName };
  }

  async deleteOutboundOrder(id: string) {
    return this.delete(`/outbound/${encodeURIComponent(id)}`);
  }

  async getOutboundOrder(idOrNumber: string) {
    return this.get(`/outbound/${encodeURIComponent(idOrNumber)}`);
  }

  async updateOutboundOrder(idOrNumber: string, data: any) {
    return this.put(`/outbound/${encodeURIComponent(idOrNumber)}`, data);
  }

  async createOutboundOrder(data: any) {
    return this.post(`/outbound`, data);
  }

  async bulkCreateOutboundOrders(orders: any[]) {
    return this.post(`/outbound/bulk`, orders);
  }

  // 批量更新出庫單狀態，支援 group=true 時自動生成單一批次並建立關聯
  async batchUpdateOutboundStatus(payload: {
    ids: string[];
    status: string;
    group?: boolean;
    batchPickingNumber?: string;
    locationFrom?: string;
    locationTo?: string;
    orderNumbers?: string[];
    orderWaybillList?: Array<{ orderNumber: string; waybillNumber: string }>;
    confirmOversell?: boolean;
    oversellMode?: 'CORRECTION' | 'SHORTAGE';
  }) {
    // 批量更新可能需要較長時間，根據訂單數量動態調整超時
    // 每 20 條訂單增加 1 分鐘，最少 3 分鐘，最多 10 分鐘
    // 例如：20條=3分鐘，50條=3分鐘，100條=5分鐘，200條=10分鐘
    const orderCount = payload.ids?.length || 0;
    const timeoutMinutes = Math.min(10, Math.max(3, Math.ceil(orderCount / 20)));
    const timeoutMs = timeoutMinutes * 60 * 1000; // 轉換為毫秒

    console.log(`[批量更新] ${orderCount} 條訂單，設置超時時間：${timeoutMinutes} 分鐘`);

    return this.request(`/outbound/batch/update-status`, {
      method: 'POST',
      body: JSON.stringify(payload),
      timeoutMs: timeoutMs
    });
  }

  // 更新取貨日期範圍（異步任務）- 支援與 /api/v1/outbound 接口相同的查詢條件
  async updatePickUpDateRange(params: { 
    pickUpDateStart: string; 
    pickUpDateEnd: string;
    search?: string;
    status?: string;
    warehouseId?: string;
    customerId?: string;
    dateFrom?: string;
    dateTo?: string;
    createdDateStart?: string;
    createdDateEnd?: string;
    storeId?: string;
    hasStockIssues?: string;
    runTagId?: string;
  }) {
    return this.post(`/outbound/update-pickup-date-range`, params);
  }

  // 查詢任務狀態
  async getTaskStatus(taskId: string) {
    return this.get(`/outbound/task/${taskId}`);
  }

  // 新增：生成運單號列表報告
  async generatePickupDateWaybillListReport(params: { 
    pickUpDateStart?: string; 
    pickUpDateEnd?: string;
    pickOrderCreateDateStart?: string;
    pickOrderCreateDateEnd?: string;
    tagIds?: string[];
    excludeTags?: boolean;
  }): Promise<{ blob: Blob; fileName: string }> {
    const qp = new URLSearchParams();
    
    if (params.pickUpDateStart) qp.append('pickUpDateStart', params.pickUpDateStart);
    if (params.pickUpDateEnd) qp.append('pickUpDateEnd', params.pickUpDateEnd);
    if (params.pickOrderCreateDateStart) qp.append('pickOrderCreateDateStart', params.pickOrderCreateDateStart);
    if (params.pickOrderCreateDateEnd) qp.append('pickOrderCreateDateEnd', params.pickOrderCreateDateEnd);
    if (params.tagIds && params.tagIds.length > 0) {
      qp.append('tagIds', params.tagIds.join(','));
    }
    if (params.excludeTags) qp.append('excludeTags', 'true');

    const token = localStorage.getItem('authToken');
    const url = `${this.baseURL}/outbound/reports/pickup-date-waybill-list${qp.toString() ? `?${qp.toString()}` : ''}`;
    
    const resp = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });
    
    if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`);
    
    const blob = await resp.blob();
    // 解析檔案名
    const disposition = resp.headers.get('Content-Disposition') || '';
    const defaultDate = params.pickUpDateStart 
      ? dayjs(params.pickUpDateStart).format('YYYY-MM-DD') 
      : new Date().toISOString().slice(0,10);
    let fileName = `pickup_date_waybill_list_${defaultDate}.xlsx`;
    
    // 嘗試從Content-Disposition頭中提取檔案名
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      fileName = decodeURIComponent(utf8Match[1]);
    } else {
      const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
      if (asciiMatch && asciiMatch[1]) {
        fileName = asciiMatch[1];
      }
    }
    
    return { blob, fileName };
  }

  // 新增：生成 HKTVmall 訪單摘要報告
  async generateHktvmallOrderSummaryReport(params: {
    storeId?: string[];
    dateStart?: string;
    dateEnd?: string;
    pickupDateStart?: string;
    pickupDateEnd?: string;
    deliveryDateStart?: string;
    deliveryDateEnd?: string;
    tagIds?: string[];
    excludeTags?: boolean;
  }): Promise<{ blob: Blob; fileName: string }> {
    const qp = new URLSearchParams();

    if (params.storeId && params.storeId.length > 0) {
      qp.append('storeId', params.storeId.join(','));
    }
    if (params.dateStart) qp.append('dateStart', params.dateStart);
    if (params.dateEnd) qp.append('dateEnd', params.dateEnd);
    if (params.pickupDateStart) qp.append('pickupDateStart', params.pickupDateStart);
    if (params.pickupDateEnd) qp.append('pickupDateEnd', params.pickupDateEnd);
    if (params.deliveryDateStart) qp.append('deliveryDateStart', params.deliveryDateStart);
    if (params.deliveryDateEnd) qp.append('deliveryDateEnd', params.deliveryDateEnd);
    if (params.tagIds && params.tagIds.length > 0) {
      qp.append('tagIds', params.tagIds.join(','));
    }
    if (params.excludeTags) qp.append('excludeTags', 'true');

    const token = localStorage.getItem('authToken');
    const url = `${this.baseURL}/outbound/reports/hktvmall-order-summary${qp.toString() ? `?${qp.toString()}` : ''}`;
    
    const resp = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });
    
    if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`);
    
    const blob = await resp.blob();
    // 解析檔案名
    const disposition = resp.headers.get('Content-Disposition') || '';
    const defaultDate = params.dateStart 
      ? dayjs(params.dateStart).format('YYYY-MM-DD') 
      : new Date().toISOString().slice(0,10);
    let fileName = `hktvmall_order_summary_${defaultDate}.xlsx`;
    
    // 嘗試從Content-Disposition頭中提取檔案名
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      fileName = decodeURIComponent(utf8Match[1]);
    } else {
      const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
      if (asciiMatch && asciiMatch[1]) {
        fileName = asciiMatch[1];
      }
    }
    
    return { blob, fileName };
  }

  // 生成 Bundle Movements 報告
  async generateBundleMovementsReport(params: {
    pickupDateStart: string;
    pickupDateEnd: string;
    storeCodes: string[];
  }): Promise<{ blob: Blob; fileName: string }> {
    const qp = new URLSearchParams();
    qp.append('pickupDateStart', params.pickupDateStart);
    qp.append('pickupDateEnd', params.pickupDateEnd);
    if (params.storeCodes.length > 0) {
      qp.append('storeCodes', params.storeCodes.join(','));
    }

    const token = localStorage.getItem('authToken');
    const url = `${this.baseURL}/outbound/reports/bundle-movements?${qp.toString()}`;

    const resp = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });

    if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`);

    const blob = await resp.blob();
    const disposition = resp.headers.get('Content-Disposition') || '';
    let fileName = `bundle-movements-${params.pickupDateStart}.xlsx`;

    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      fileName = decodeURIComponent(utf8Match[1]);
    } else {
      const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
      if (asciiMatch && asciiMatch[1]) {
        fileName = asciiMatch[1];
      }
    }

    return { blob, fileName };
  }

  // Waybills (delivery)
  async getWaybills(params?: { page?: number; limit?: number; search?: string; status?: string; dateFrom?: string; dateTo?: string; sortField?: string; sortOrder?: 'asc' | 'desc' }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.search) qp.append('search', params.search);
    if (params?.status) qp.append('status', params.status);
    if (params?.dateFrom) qp.append('dateFrom', params.dateFrom);
    if (params?.dateTo) qp.append('dateTo', params.dateTo);
    if (params?.sortField) qp.append('sortField', params.sortField);
    if (params?.sortOrder) qp.append('sortOrder', params.sortOrder);
    const q = qp.toString();
    return this.get(`/waybills${q ? `?${q}` : ''}`);
  }

  // 新增：生成 Product Movement Summary by Customer 報告（現在指向 StockController）
  async generateProductMovementSummary(params: {
    dateStart: string;
    dateEnd: string;
    dateType?: 'created' | 'pickup'; // 'created' = 建立日期, 'pickup' = 取貨日期
    customerName?: string; // 客戶名稱篩選
    storeCode?: string; // Store Code 篩選
    onlyInOut?: boolean; // 只包含實際出入庫記錄（略過未出貨的 PENDING 訂單）
  }): Promise<{ blob: Blob; fileName: string }> {
    const qp = new URLSearchParams();

    if (params.dateStart) qp.append('dateStart', params.dateStart);
    if (params.dateEnd) qp.append('dateEnd', params.dateEnd);
    if (params.dateType) qp.append('dateType', params.dateType);
    if (params.customerName) qp.append('customerName', params.customerName);
    if (params.storeCode) qp.append('storeCode', params.storeCode);
    // Only sent when ticked, so an unticked box leaves the request byte-identical
    // to what it was before this option existed.
    if (params.onlyInOut) qp.append('onlyInOut', 'true');

    const token = localStorage.getItem('authToken');
    const url = `${this.baseURL}/stock-management/reports/product-movement-summary${qp.toString() ? `?${qp.toString()}` : ''}`;

    const resp = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });

    if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`);

    const blob = await resp.blob();
    // 解析檔案名
    const disposition = resp.headers.get('Content-Disposition') || '';

    let fileName = `product_movement_summary_${params.dateStart}_to_${params.dateEnd}.xlsx`;

    // 嘗試從Content-Disposition頭中提取檔案名
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      fileName = decodeURIComponent(utf8Match[1]);
    } else {
      const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
      if (asciiMatch && asciiMatch[1]) {
        fileName = asciiMatch[1];
      }
    }

    return { blob, fileName };
  }

  // Packing management
  async getPackingOrders(params?: { page?: number; limit?: number; search?: string; status?: string; dateFrom?: string; dateTo?: string; sortField?: string; sortOrder?: 'asc' | 'desc' }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.search) qp.append('search', params.search);
    if (params?.status) qp.append('status', params.status);
    if (params?.dateFrom) qp.append('dateFrom', params.dateFrom);
    if (params?.dateTo) qp.append('dateTo', params.dateTo);
    if (params?.sortField) qp.append('sortField', params.sortField);
    if (params?.sortOrder) qp.append('sortOrder', params.sortOrder);
    const q = qp.toString();
    return this.get(`/packing${q ? `?${q}` : ''}`);
  }

  async getPackingPickups(params?: { page?: number; limit?: number; search?: string; status?: string; dateFrom?: string; dateTo?: string; sortField?: string; sortOrder?: 'asc' | 'desc' }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.search) qp.append('search', params.search);
    if (params?.status) qp.append('status', params.status);
    if (params?.dateFrom) qp.append('dateFrom', params.dateFrom);
    if (params?.dateTo) qp.append('dateTo', params.dateTo);
    if (params?.sortField) qp.append('sortField', params.sortField);
    if (params?.sortOrder) qp.append('sortOrder', params.sortOrder);
    const q = qp.toString();
    return this.get(`/packing/pickups${q ? `?${q}` : ''}`);
  }

  async confirmPackingPickup(id: string, data?: { pickedUpBy?: string }) {
    return this.patch(`/packing/${encodeURIComponent(id)}/pickup`, data || {});
  }

  async getPackingOrderDetail(id: string) {
    return this.get(`/packing/${encodeURIComponent(id)}`);
  }

  async startPacking(id: string, data?: { user?: string }) {
    return this.patch(`/packing/${encodeURIComponent(id)}/start`, data || {});
  }

  async markPackingPacked(id: string) {
    return this.patch(`/packing/${encodeURIComponent(id)}/packed`, {});
  }

  async markPackingReady(id: string) {
    return this.patch(`/packing/${encodeURIComponent(id)}/ready`, {});
  }

  /**
   * OPTIMIZED: Fast barcode scan for pickup management
   * Single API call that handles: lookup, validation, status update
   * Returns immediately, processes inventory in background
   */
  async scanForPickup(barcode: string): Promise<{
    success: boolean;
    message: string;
    order?: any;
    timing?: { queryMs: number; totalMs: number };
  }> {
    return this.post('/packing/scan-pickup', { barcode });
  }

  async getPackingItems(orderId: string) {
    return this.get(`/packing/${encodeURIComponent(orderId)}/items`);
  }

  async upsertPackingItems(orderId: string, items: any[]) {
    return this.post(`/packing/${encodeURIComponent(orderId)}/items`, { items });
  }

  // Split packing/order by orderNo
  async splitOrderByOrderNo(orderNo: string, options?: { strategy?: string; dryRun?: boolean }) {
    if (!orderNo || !String(orderNo).trim()) {
      throw new Error('orderNo is required');
    }
    const payload: any = { orderNo: String(orderNo).trim(), ...(options || {}) };
    return this.post(`/packing/split-order`, payload);
  }

  async getWaybillById(id: string) {
    return this.get(`/waybills/${encodeURIComponent(id)}`);
  }

  async createWaybill(data: any) {
    return this.post('/waybills', data);
  }

  async updateWaybill(id: string, data: any) {
    return this.put(`/waybills/${encodeURIComponent(id)}`, data);
  }

  async deleteWaybill(id: string) {
    return this.delete(`/waybills/${encodeURIComponent(id)}`);
  }

  // ===== IMPORT/EXPORT ENDPOINTS =====

  // Download import templates
  async downloadImportTemplate(entity: string) {
    const response = await fetch(`${this.baseURL}/import/templates/${entity}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.blob();
  }

  // Get import history
  async getImportHistory(page = 1, limit = 20) {
    return this.get(`/import/history?page=${page}&limit=${limit}`);
  }

  // Validate import data (before actual import)
  async validateImportData(entity: string, data: any, mapping: any) {
    return this.post(`/import/${entity}/validate`, { data, mapping, validateOnly: true });
  }

  // Perform mass import
  async performMassImport(entity: string, data: any, mapping: any) {
    return this.post(`/import/${entity}/import`, { data, mapping, validateOnly: false });
  }

  // ===== INTEGRATION ENDPOINTS =====

  // Get all integrations
  async getIntegrations() {
    return this.get('/integrations');
  }

  // Shopify - Orders
  async getShopifyOrders(params?: { q?: string; financialStatus?: string; fulfillmentStatus?: string; page?: number; limit?: number }) {
    const qp = new URLSearchParams();
    if (params?.q) qp.append('q', params.q);
    if (params?.financialStatus) qp.append('financialStatus', params.financialStatus);
    if (params?.fulfillmentStatus) qp.append('fulfillmentStatus', params.fulfillmentStatus);
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    const q = qp.toString();
    return this.get(`/shopify/orders${q ? `?${q}` : ''}`);
  }

  async getShopifyOrderById(id: string) {
    return this.get(`/shopify/orders/${encodeURIComponent(id)}`);
  }

  async pullShopifyOrders(params: { shop: string; since?: string; limit?: number }) {
    const qp = new URLSearchParams();
    qp.append('shop', params.shop);
    if (params.since) qp.append('since', params.since);
    if (params.limit) qp.append('limit', String(params.limit));
    const q = qp.toString();
    return this.get(`/shopify/orders/pull${q ? `?${q}` : ''}`);
  }

  // Shopify - Products
  async getShopifyProducts(params?: { q?: string; status?: string; vendor?: string; page?: number; limit?: number }) {
    const qp = new URLSearchParams();
    if (params?.q) qp.append('q', params.q);
    if (params?.status) qp.append('status', params.status);
    if (params?.vendor) qp.append('vendor', params.vendor);
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    const q = qp.toString();
    return this.get(`/shopify/products${q ? `?${q}` : ''}`);
  }

  async getShopifyProductById(id: string | number) {
    return this.get(`/shopify/products/${encodeURIComponent(String(id))}`);
  }

  async getShopifyDashboard() {
    return this.get(`/shopify/dashboard`);
  }

  // Shopify Missing SKUs
  async getShopifyMissingSkus(params?: { page?: number; limit?: number; q?: string; days?: number }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.q) qp.append('q', params.q);
    if (params?.days) qp.append('days', String(params.days));
    const q = qp.toString();
    return this.get(`/shopify/missing-skus${q ? `?${q}` : ''}`);
  }

  async createProductsFromMissingSkusShopify(payload: { companyId: string; skus: Array<{ skuId: string; itemNameZh?: string; itemNameEn?: string; brand?: string }> }) {
    return this.post('/shopify/missing-skus/create-products', payload);
  }

  // HKTVmall - Missing SKUs
  async getHKTVMissingSkus(params?: { page?: number; limit?: number; q?: string; days?: number }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', String(params.page));
    if (params?.limit) qp.append('limit', String(params.limit));
    if (params?.q) qp.append('q', params.q);
    if (params?.days) qp.append('days', String(params.days));
    const q = qp.toString();
    return this.get(`/hktvmall/missing-skus${q ? `?${q}` : ''}`);
  }

  async createProductsFromMissingSkus(payload: { companyId: string; skus: Array<{ skuId: string; itemNameZh?: string; itemNameEn?: string; brand?: string }> }) {
    return this.post('/hktvmall/missing-skus/create-products', payload);
  }

  // HKTVmall Run Tags (DB)
  async listHKTVRunTags() {
    return this.get(`/hktvmall/run-tags/list`);
  }
  async upsertHKTVRunTag(payload: { tagName: string; storeCodes: string[]; createdBy?: string }) {
    return this.post(`/hktvmall/run-tags`, payload);
  }
  async deleteHKTVRunTag(tagName: string) {
    return this.delete(`/hktvmall/run-tags/${encodeURIComponent(tagName)}`);
  }

  // Test integration connection
  async testIntegration(integrationId: string) {
    return this.post(`/integrations/${integrationId}/test`, {});
  }

  // Webhook management
  async createWebhook(webhookData: any) {
    return this.post('/integrations/webhooks', webhookData);
  }

  async getWebhooks() {
    return this.get('/integrations/webhooks');
  }

  // N8N integration
  async setupN8nIntegration(n8nConfig: any) {
    return this.post('/integrations/n8n/setup', n8nConfig);
  }

  async triggerN8nWorkflow(integrationId: string, workflowId: string, data: any) {
    return this.post('/integrations/n8n/trigger', { integrationId, workflowId, data });
  }

  // Google Sheets integration
  async setupGoogleSheetsIntegration(sheetsConfig: any) {
    return this.post('/integrations/google-sheets/setup', sheetsConfig);
  }

  async syncWithGoogleSheets(integrationId: string, sheetName: string, direction: 'import' | 'export') {
    return this.post('/integrations/google-sheets/sync', { integrationId, sheetName, direction });
  }

  // Custom integrations
  async createCustomIntegration(integrationData: any) {
    return this.post('/integrations/custom', integrationData);
  }

  async executeCustomIntegration(integrationId: string, endpoint: any, data: any, params?: any) {
    return this.post('/integrations/custom/execute', { integrationId, endpoint, data, params });
  }


  /**
   * 获取入库凭证HTML内容（用于前端PDF生成）
   */
  async getInboundVoucherHTML(orderNumber?: string): Promise<string> {
    try {
      const endpoint = orderNumber ?
          `/pdf/inbound-voucher/getHtml?orderNumber=${encodeURIComponent(orderNumber)}` :
          '/pdf/inbound-voucher/getHtml';

      const url = `${this.baseURL}${endpoint}`;
      const token = localStorage.getItem('authToken');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/html; charset=utf-8',
          ...(token ? {'Authorization': `Bearer ${token}`} : {})
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const htmlContent = await response.text();
      return htmlContent;
    } catch (error) {
      console.error('Error getting inbound voucher HTML:', error);
      throw error;
    }
  }


  async getInboundBeePDF(orderNumber?: string): Promise<string> {
    try {
      const endpoint = orderNumber ?
          `/pdf/inbound-bee/getHtml?orderNumber=${encodeURIComponent(orderNumber)}` :
          '/pdf/inbound-bee/getHtml';

      const url = `${this.baseURL}${endpoint}`;
      const token = localStorage.getItem('authToken');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/html; charset=utf-8',
          ...(token ? {'Authorization': `Bearer ${token}`} : {})
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const htmlContent = await response.text();
      return htmlContent;
    } catch (error) {
      console.error('Error downloading inbound bee PDF:', error);
      throw error;
    }

  }

  /**
   * 入庫憑證 PDF 下載 (PDFKit版本 - 支持多頁重複表頭)
   */
  async downloadInboundBeePDFKit(orderNumber: string): Promise<Blob> {
    try {
      const endpoint = `/pdf/inbound-bee/getPdf?orderNumber=${encodeURIComponent(orderNumber)}`;
      const url = `${this.baseURL}${endpoint}`;
      const token = localStorage.getItem('authToken');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...(token ? {'Authorization': `Bearer ${token}`} : {})
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.blob();
    } catch (error) {
      console.error('Error downloading inbound bee PDF (PDFKit):', error);
      throw error;
    }
  }

  async getInboundBeeExcel(orderNumber: string): Promise<Blob> {
    try {
      const endpoint = `/pdf/inbound-bee/getExcel?orderNumber=${encodeURIComponent(orderNumber)}`;
      const url = `${this.baseURL}${endpoint}`;
      const token = localStorage.getItem('authToken');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...(token ? {'Authorization': `Bearer ${token}`} : {})
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error('Error getting inbound bee Excel:', error);
      throw error;
    }
  }

  async generateInboundVoucherHTML(orderNumber?: string): Promise<string> {
    try {
      const endpoint = orderNumber ?
          `/pdf/inbound-voucher?orderNumber=${encodeURIComponent(orderNumber)}` :
          '/pdf/inbound-voucher';
      const url = `${this.baseURL}${endpoint}`;
      const token = localStorage.getItem('authToken');
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'text/html; charset=utf-8',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.text();
    } catch (error) {
      console.error('Error generating inbound voucher HTML:', error);
      throw error;
    }
  }

  async downloadPickingOrderPDF(params?: { batchNumber?: string; pickOrderNumber?: string }): Promise<{ blob: Blob; fileName: string }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.batchNumber) queryParams.append('batchNumber', params.batchNumber);
      if (params?.pickOrderNumber) queryParams.append('pickOrderNumber', params.pickOrderNumber);
      const endpoint = `/pdf/picking-order/download${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const url = `${this.baseURL}${endpoint}`;
      const token = localStorage.getItem('authToken');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/pdf',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const disposition = response.headers.get('Content-Disposition') || '';
      let fileName = 'picking-order.pdf';
      const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
      if (match) {
        fileName = decodeURIComponent(match[1] || match[2]);
      }
      const blob = await response.blob();
      return { blob, fileName };
    } catch (error) {
      console.error('Error downloading picking order PDF:', error);
      throw error;
    }
  }

  /**
   * Download Waybill PDF (uses backend PDFKit generation with repeating table headers)
   * GET /api/pdf/waybill/download
   */
  async downloadWaybillPDF(params: { waybillNumber?: string; orderNumber?: string }): Promise<{ blob: Blob; fileName: string }> {
    try {
      const queryParams = new URLSearchParams();
      if (params.waybillNumber) queryParams.append('waybillNumber', params.waybillNumber);
      if (params.orderNumber) queryParams.append('orderNumber', params.orderNumber);
      const endpoint = `/pdf/waybill/download${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const url = `${this.baseURL}${endpoint}`;
      const token = localStorage.getItem('authToken');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/pdf',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const disposition = response.headers.get('Content-Disposition') || '';
      let fileName = `waybill-${params.waybillNumber || params.orderNumber || 'download'}.pdf`;
      const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
      if (match) {
        fileName = decodeURIComponent(match[1] || match[2]);
      }
      const blob = await response.blob();
      return { blob, fileName };
    } catch (error) {
      console.error('Error downloading waybill PDF:', error);
      throw error;
    }
  }

  async getPrintLabelHTMLBatch(params: { id: string }): Promise<{ items: { fileName: string; html: string }[] }> {
    const queryParams = new URLSearchParams();
    if (params.id) queryParams.append('id', params.id);
    const endpoint = `/pdf/print-label/html-batch${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    return { items: data?.items || [] };
  }

  async downloadPrintLabelZIP(params: { id: string }): Promise<{ blob: Blob; fileName: string }> {
    const qp = new URLSearchParams();
    if (params.id) qp.append('id', params.id);
    const endpoint = `/pdf/print-label/zip${qp.toString() ? `?${qp.toString()}` : ''}`;
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('authToken');
    const resp = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`);
    const blob = await resp.blob();
    const disposition = resp.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const fileName = match ? decodeURIComponent(match[1] || match[2]) : `print-labels-${params.id}.zip`;
    return { blob, fileName };
  }

  async downloadPrintLabelCombined(params: { id: string }): Promise<{ blob: Blob; fileName: string }> {
    const qp = new URLSearchParams();
    if (params.id) qp.append('id', params.id);
    const endpoint = `/pdf/print-label/combined${qp.toString() ? `?${qp.toString()}` : ''}`;
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('authToken');
    const resp = await fetch(url, {
      method: 'GET',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`);
    const blob = await resp.blob();
    const disposition = resp.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const fileName = match ? decodeURIComponent(match[1] || match[2]) : `pick-labels-${params.id}.pdf`;
    return { blob, fileName };
  }

  async generatePickingOrderHTML(params?: { batchNumber?: string; pickOrderNumber?: string }): Promise<string> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.batchNumber) queryParams.append('batchNumber', params.batchNumber);
      if (params?.pickOrderNumber) queryParams.append('pickOrderNumber', params.pickOrderNumber);
      const endpoint = `/pdf/picking-order${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const url = `${this.baseURL}${endpoint}`;
      const token = localStorage.getItem('authToken');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/html; charset=utf-8',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.text();
    } catch (error) {
      console.error('Error generating picking order HTML:', error);
      throw error;
    }
  }

  /**
   * getHktvMallWaybillHtml【是HKTVmall的waybill】
   * @param orderNumber
   * @param waybillNumber
   */
  async getHktvMallWaybillHtml(orderNumber:string, waybillNumber: string): Promise<string> {
    try {
      const endpoint = `/pdf/getHktvMallWaybillHtml?orderNumber=${encodeURIComponent(orderNumber)}&waybillNumber=${encodeURIComponent(waybillNumber)}`;
      const url = `${this.baseURL}${endpoint}`;
      const token = localStorage.getItem('authToken');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/html; charset=utf-8',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const htmlContent = await response.text();
      return htmlContent;
    } catch (error) {
      console.error('Error getting inbound voucher HTML:', error);
      throw error;
    }
  }

  /**
   * HKTVmall 運單 HTML 批量（返回 { waybillNo: html }）
   */
  async getHktvMallWaybillHtmlBatch(params: { orderNumber: string }): Promise<Record<string, string>> {
    const endpoint = `/pdf/getHktvMallWaybillHtmlBatch`;
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('authToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ orderNumber: params.orderNumber })
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();

    const rawItems = data?.items;
    const map: Record<string, string> = {};
    if (Array.isArray(rawItems)) {
      for (const it of rawItems) {
        const key = it && typeof it.waybillNumber === 'string' ? it.waybillNumber : undefined;
        const val = it && typeof it.html === 'string' ? it.html : undefined;
        if (key && val) map[key] = val;
      }
    }
    return map;
  }

  /**
   * getWaybillHtml【不是HKTVmall的waybill】
   * @param orderNumber
   */
  async getWaybillHtml(orderNumber:string, waybillNumber: string): Promise<string> {
    try {
      const endpoint = `/pdf/getWaybillHtml?orderNumber=${encodeURIComponent(orderNumber)}&waybillNumber=${encodeURIComponent(waybillNumber)}`;
      const url = `${this.baseURL}${endpoint}`;
      const token = localStorage.getItem('authToken');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/html; charset=utf-8',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const htmlContent = await response.text();
      return htmlContent;
    } catch (error) {
      console.error('Error getting inbound voucher HTML:', error);
      throw error;
    }
  }

  /**
   * 揀貨單 HTML（用於客戶端PDF生成）
   * @param batchNumber 批次編號
   */
  async getPickingOrderHTML(batchNumber: string): Promise<string> {
    try {
      const endpoint = `/pdf/picking-order?batchNumber=${encodeURIComponent(batchNumber)}`;
      const url = `${this.baseURL}${endpoint}`;
      const token = localStorage.getItem('authToken');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/html; charset=utf-8',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const htmlContent = await response.text();
      return htmlContent;
    } catch (error) {
      console.error('Error getting picking order HTML:', error);
      throw error;
    }
  }

  async downloadLocalWaybillPDF(waybillNumber?: string, orderNumber?: string): Promise<void> {
    const qp = new URLSearchParams();
    if (waybillNumber) qp.set('waybillNumber', waybillNumber);
    if (orderNumber) qp.set('orderNumber', orderNumber);
    const url = `${this.baseURL}/pdf/waybill/download${qp.toString() ? `?${qp.toString()}` : ''}`;
    const token = localStorage.getItem('authToken');
    const resp = await fetch(url, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } });
    if (!resp.ok) {
      const err = await resp.text().catch(() => '');
      throw new Error(`Fallback failed ${resp.status}: ${err.slice(0, 200)}`);
    }
    const blob = await resp.blob();
    const safe = (waybillNumber || orderNumber || 'waybill').trim().replace(/[^\w.-]+/g, '_');
    const fileName = `waybill-${safe}.pdf`;
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(href);
  }

  /**
   * 批量運單 PDF（合併多個運單為單一 PDF 並在新標籤頁打開）
   * POST /api/pdf/waybills/batch-combined
   * @param orderIds 訂單 ID 列表
   */
  async downloadCombinedWaybillsPDF(orderIds: string[]): Promise<void> {
    const url = `${this.baseURL}/pdf/waybills/batch-combined`;
    const token = localStorage.getItem('authToken');
    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ orderIds })
    });
    if (!resp.ok) {
      const err = await resp.text().catch(() => '');
      throw new Error(`Combined waybill PDF failed ${resp.status}: ${err.slice(0, 200)}`);
    }
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    // Open in new tab
    const newTab = window.open(blobUrl, '_blank');
    if (newTab) {
      // Clean up blob URL after a delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } else {
      // Fallback: download if popup blocked
      const timestamp = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `Waybills_Combined_${timestamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    }
  }

  /**
   * Get order-waybill list for a batch picking (for client-side PDF generation)
   * GET /api/pdf/waybills/batch-list?batchId=xxx
   * @param batchId Batch picking ID
   * @returns { orderWaybillList: Array<{ orderNumber, waybillNumber, deliveryProvider, outboundOrderId, storeCode, hasFedExLabel, hasSFExpressLabel, sfExpressWaybillPdf }>, totalOrders: number }
   */
  async getBatchWaybillList(batchId: string): Promise<{
    orderWaybillList: Array<{
      orderNumber: string;
      waybillNumber: string;
      deliveryProvider?: string;
      outboundOrderId?: string;
      storeCode?: string;
      hasFedExLabel?: boolean;
      hasSFExpressLabel?: boolean;
      sfExpressWaybillPdf?: string;
    }>;
    totalOrders: number
  }> {
    const resp = await this.get(`/pdf/waybills/batch-list?batchId=${encodeURIComponent(batchId)}`);
    return resp as {
      orderWaybillList: Array<{
        orderNumber: string;
        waybillNumber: string;
        deliveryProvider?: string;
        outboundOrderId?: string;
        storeCode?: string;
        hasFedExLabel?: boolean;
        hasSFExpressLabel?: boolean;
        sfExpressWaybillPdf?: string;
      }>;
      totalOrders: number
    };
  }

  /**
   * Download batch waybill PDF using batch picking ID
   * POST /api/pdf/waybills/batch-combined
   * @param batchId Batch picking ID
   * @returns { blob, fileName }
   */
  async downloadBatchWaybillPDF(params: { batchId: string }): Promise<{ blob: Blob; fileName: string }> {
    const url = `${this.baseURL}/pdf/waybills/batch-combined`;
    const token = localStorage.getItem('authToken');
    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ batchId: params.batchId })
    });
    if (!resp.ok) {
      const err = await resp.text().catch(() => '');
      throw new Error(`Batch waybill PDF failed ${resp.status}: ${err.slice(0, 200)}`);
    }

    // Extract filename from Content-Disposition header if available
    const contentDisposition = resp.headers.get('Content-Disposition');
    let fileName = `Waybills_Batch_${params.batchId}_${new Date().toISOString().slice(0, 10)}.pdf`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+)"?/);
      if (match && match[1]) {
        fileName = match[1];
      }
    }

    const blob = await resp.blob();
    return { blob, fileName };
  }

  async generateWaybillHTML(params?: Record<string, string | number | undefined>): Promise<string> {
    try {
      const queryParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null) queryParams.append(k, String(v));
        });
      }
      const endpoint = `/pdf/waybill${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const url = `${this.baseURL}${endpoint}`;
      const token = localStorage.getItem('authToken');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/html; charset=utf-8',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.text();
    } catch (error) {
      console.error('Error generating waybill HTML:', error);
      throw error;
    }
  }

  // ===== HKTV Orders (by tracking) =====
  async getHKTVOrderByTrackingId(trackingId: string): Promise<any> {
    const url = `${this.baseURL}/hktv-orders/tracking/${encodeURIComponent(trackingId)}`;
    const token = localStorage.getItem('authToken');
    const resp = await fetch(url, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } });
    if (!resp.ok) {
      const txt = await resp.text().catch(() => '');
      throw new Error(`HTTP ${resp.status}: ${txt.slice(0, 200)}`);
    }
    return resp.json();
  }

  async generateDeliveryNoteHTML(params: {
    customerName: string;
    deliveryDate: string;
    deliveryTime: string;
    deliveryOrderNumber: string;
    receiverName?: string;
    receiverContact?: string;
    deliveryAddress: string;
    deliveryItemNumber: string;
    orderNumber: string;
  }): Promise<string> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('customerName', params.customerName);
      queryParams.append('deliveryDate', params.deliveryDate);
      queryParams.append('deliveryTime', params.deliveryTime);
      queryParams.append('deliveryOrderNumber', params.deliveryOrderNumber);
      if (params.receiverName) queryParams.append('receiverName', params.receiverName);
      if (params.receiverContact) queryParams.append('receiverContact', params.receiverContact);
      queryParams.append('deliveryAddress', params.deliveryAddress);
      queryParams.append('deliveryItemNumber', params.deliveryItemNumber);
      queryParams.append('orderNumber', params.orderNumber);
      const endpoint = `/pdf/delivery-note?${queryParams.toString()}`;
      const url = `${this.baseURL}${endpoint}`;
      const token = localStorage.getItem('authToken');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/html; charset=utf-8',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.text();
    } catch (error) {
      console.error('Error generating delivery note HTML:', error);
      throw error;
    }
  }

  // ===== Warehouse Analytics =====
  async getWarehouseAnalytics(params?: { warehouseId?: string; dateFrom?: string; dateTo?: string; companyId?: string }) {
    const qp = new URLSearchParams();
    if (params?.warehouseId) qp.append('warehouseId', params.warehouseId);
    if (params?.dateFrom) qp.append('dateFrom', params.dateFrom);
    if (params?.dateTo) qp.append('dateTo', params.dateTo);
    if (params?.companyId) qp.append('companyId', params.companyId);
    const q = qp.toString();
    return this.get(`/warehouse-analytics${q ? `?${q}` : ''}`);
  }

  // Picking KPIs
  async getPickingKPI(): Promise<{ data: { total: number; pending: number; processing: number; allocating: number; verifying: number } }> {
    return this.get('/picking/kpi/summary');
  }

  // Packing KPIs
  async getPackingKPI(): Promise<{ data: { total: number; packing: number; ready: number; shipped: number; delivered: number } }> {
    return this.get('/packing/kpi/summary');
  }

  // HKTV Run Tags with Warehouse IDs
  async getRunTagsWithWarehouseIds(): Promise<{ success: boolean; data: Array<{ id: string; tagName: string; storeCodes: string[]; warehouseIds: string[] }> }> {
    return this.get('/hktvmall/run-tags/with-warehouse-ids');
  }

  // ===== Audit Logs =====
  async getAuditLogs(params?: {
    page?: number;
    limit?: number;
    module?: string;
    actionType?: string;
    riskLevel?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    searchText?: string;
    userId?: string;
  }) {
    const qp = new URLSearchParams();
    if (params?.page) qp.append('page', params.page.toString());
    if (params?.limit) qp.append('limit', params.limit.toString());
    if (params?.module) qp.append('module', params.module);
    if (params?.actionType) qp.append('actionType', params.actionType);
    if (params?.riskLevel) qp.append('riskLevel', params.riskLevel);
    if (params?.status) qp.append('status', params.status);
    if (params?.startDate) qp.append('startDate', params.startDate);
    if (params?.endDate) qp.append('endDate', params.endDate);
    if (params?.searchText) qp.append('searchText', params.searchText);
    if (params?.userId) qp.append('userId', params.userId);
    const q = qp.toString();
    return this.get(`/audit-logs${q ? `?${q}` : ''}`);
  }

  async getAuditLogById(id: string) {
    return this.get(`/audit-logs/${id}`);
  }

  async getAuditStatistics(params?: {
    module?: string;
    startDate?: string;
    endDate?: string;
  }) {
    const qp = new URLSearchParams();
    if (params?.module) qp.append('module', params.module);
    if (params?.startDate) qp.append('startDate', params.startDate);
    if (params?.endDate) qp.append('endDate', params.endDate);
    const q = qp.toString();
    return this.get(`/audit-logs/statistics${q ? `?${q}` : ''}`);
  }

  async exportAuditLogs(params?: {
    module?: string;
    actionType?: string;
    riskLevel?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    searchText?: string;
  }) {
    const qp = new URLSearchParams();
    if (params?.module) qp.append('module', params.module);
    if (params?.actionType) qp.append('actionType', params.actionType);
    if (params?.riskLevel) qp.append('riskLevel', params.riskLevel);
    if (params?.status) qp.append('status', params.status);
    if (params?.startDate) qp.append('startDate', params.startDate);
    if (params?.endDate) qp.append('endDate', params.endDate);
    if (params?.searchText) qp.append('searchText', params.searchText);
    const q = qp.toString();
    
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${this.baseURL}/audit-logs/export${q ? `?${q}` : ''}`, {
      method: 'GET',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });
    
    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }
    
    return response.blob();
  }

  // ==================== Gobuyship Delivery Tracking ====================

  /**
   * Get all delivery status codes with localized names
   */
  async getGobuyshipStatusCodes() {
    return this.get('/gobuyship/status-codes');
  }

  /**
   * Query delivery status directly by tracking number
   */
  async getGobuyshipDeliveryStatus(trackingNo: string) {
    return this.get(`/gobuyship/track/${encodeURIComponent(trackingNo)}`);
  }

  /**
   * Get delivery tracking info for an outbound order
   */
  async getOutboundDeliveryTracking(outboundOrderId: string) {
    return this.get(`/gobuyship/outbound/${encodeURIComponent(outboundOrderId)}/tracking`);
  }

  /**
   * Set tracking number for an outbound order
   */
  async setOutboundTrackingNumber(outboundOrderId: string, trackingNo: string, autoRefresh: boolean = true) {
    return this.post(`/gobuyship/outbound/${encodeURIComponent(outboundOrderId)}/tracking`, {
      trackingNo,
      autoRefresh,
    });
  }

  /**
   * Refresh delivery status for an outbound order
   */
  async refreshOutboundDeliveryStatus(outboundOrderId: string) {
    return this.post(`/gobuyship/outbound/${encodeURIComponent(outboundOrderId)}/refresh`, {});
  }

  /**
   * Batch refresh delivery status for multiple outbound orders
   */
  async batchRefreshDeliveryStatus(outboundOrderIds: string[]) {
    return this.post('/gobuyship/outbound/batch-refresh', {
      outboundOrderIds,
    });
  }

  // ==================== SF Express Delivery Integration ====================

  /**
   * Get SF Express configuration for the current company
   * @param accountType - 'standard' (default) or 'b2b'
   */
  async getSFExpressConfig(accountType: 'standard' | 'b2b' = 'standard') {
    return this.get(`/sf-express/config?accountType=${accountType}`);
  }

  /**
   * Save SF Express configuration for the current company
   * @param config - Configuration including accountType ('standard' or 'b2b')
   */
  async saveSFExpressConfig(config: {
    clientCode: string;
    checkwordSandbox?: string;
    checkwordProd: string;
    custId?: string;
    templateCode?: string;
    isProduction: boolean;
    isActive: boolean;
    accountType?: 'standard' | 'b2b';
  }) {
    return this.post('/sf-express/config', config);
  }

  /**
   * Track a SF Express shipment directly by tracking number
   */
  async trackSFExpressShipment(trackingNumber: string) {
    return this.get(`/sf-express/tracking/${encodeURIComponent(trackingNumber)}`);
  }

  /**
   * Get SF Express tracking info for an outbound order
   */
  async getSFExpressOutboundTracking(outboundOrderId: string) {
    return this.get(`/sf-express/outbound/${encodeURIComponent(outboundOrderId)}/tracking`);
  }

  /**
   * Refresh SF Express tracking status for an outbound order
   */
  async refreshSFExpressOutboundTracking(outboundOrderId: string) {
    return this.post(`/sf-express/outbound/${encodeURIComponent(outboundOrderId)}/refresh`, {});
  }

  /**
   * Create a SF Express shipment for an outbound order
   * This gets recipient info from the order and creates a shipment with waybill PDF
   */
  async createSFExpressOutboundShipment(
    outboundOrderId: string,
    shipperInfo: {
      name: string;
      address: string;
      phone: string;
      province?: string;
      city?: string;
      cargoName?: string;
    },
    options?: {
      expressTypeId?: number; // 1=T4 (Next Day), 2=T6 (Standard), 6=T104 (Same Day)
      payMethod?: number; // 1=Prepaid, 2=COD, 3=Monthly
      recipientInfo?: {
        name: string;
        address: string;
        phone: string;
        province?: string;
        city?: string;
      };
      accountType?: 'standard' | 'b2b'; // Which SF Express account to use (legacy)
      configId?: string; // New: pick a specific SF config row (multi-config flow)
      declaredValue?: string; // Declared value for cross-border customs
      declaredValueCurrency?: string; // Currency code (HKD, CNY, USD, MOP)
    }
  ) {
    return this.post(`/sf-express/outbound/${encodeURIComponent(outboundOrderId)}/shipment`, {
      shipper: shipperInfo,
      ...options,
    });
  }

  // === Carrier options (for the shipment modal) ===
  async getCarrierOptionsForOrder(orderId: string) {
    return this.get(`/outbound-orders/${encodeURIComponent(orderId)}/carrier-options`);
  }

  // === SF Express configs (multi-config) ===
  async listSFExpressConfigs() {
    return this.get('/sf-express/configs');
  }
  async getSFExpressConfigById(id: string) {
    return this.get(`/sf-express/configs/${encodeURIComponent(id)}`);
  }
  async createSFExpressConfig(payload: any) {
    return this.post('/sf-express/configs', payload);
  }
  async updateSFExpressConfigById(id: string, payload: any) {
    return this.put(`/sf-express/configs/${encodeURIComponent(id)}`, payload);
  }
  async deleteSFExpressConfigById(id: string) {
    return this.delete(`/sf-express/configs/${encodeURIComponent(id)}`);
  }

  // === FedEx configs (multi-config) ===
  async listFedExConfigs() {
    return this.get('/fedex/configs');
  }
  async getFedExConfigById(id: string) {
    return this.get(`/fedex/configs/${encodeURIComponent(id)}`);
  }
  async createFedExConfig(payload: any) {
    return this.post('/fedex/configs', payload);
  }
  async updateFedExConfigById(id: string, payload: any) {
    return this.put(`/fedex/configs/${encodeURIComponent(id)}`, payload);
  }
  async deleteFedExConfigById(id: string) {
    return this.delete(`/fedex/configs/${encodeURIComponent(id)}`);
  }

  /**
   * Get SF Express waybill PDF for an outbound order
   * @param format - 'base64' returns JSON with base64 PDF, 'pdf' returns raw PDF file
   */
  async getSFExpressOutboundLabel(outboundOrderId: string, format: 'base64' | 'pdf' = 'base64') {
    if (format === 'pdf') {
      // Return as blob for direct download
      const response = await fetch(`${API_BASE_URL}/sf-express/outbound/${encodeURIComponent(outboundOrderId)}/label?format=pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to download waybill');
      }
      return response.blob();
    }
    return this.get(`/sf-express/outbound/${encodeURIComponent(outboundOrderId)}/label?format=base64`);
  }

  /**
   * Retry downloading SF Express waybill PDF for an outbound order
   * Use this when PDF wasn't available during order creation or needs to be regenerated
   */
  async retrySFExpressOutboundLabel(outboundOrderId: string, accountType?: 'standard' | 'b2b') {
    return this.post(`/sf-express/outbound/${encodeURIComponent(outboundOrderId)}/retry-label`, {
      accountType,
    });
  }

  /**
   * Cancel SF Express shipment
   */
  async cancelSFExpressShipment(orderId: string, orderNumber: string, trackingNumber: string) {
    return this.post('/sf-express/cancel-shipment', {
      orderId,
      orderNumber,
      trackingNumber,
    });
  }

  // ==================== FedEx Delivery Tracking ====================

  /**
   * Get FedEx configuration for the current company
   */
  async getFedExConfig() {
    return this.get('/fedex/config');
  }

  /**
   * Save FedEx configuration for the current company
   */
  async saveFedExConfig(config: {
    clientId: string;
    clientSecret?: string;
    accountNumber: string;
    isProduction: boolean;
    isActive: boolean;
  }) {
    return this.post('/fedex/config', config);
  }

  /**
   * Track a FedEx shipment directly by tracking number
   */
  async trackFedExShipment(trackingNumber: string, detailed: boolean = true) {
    return this.get(`/fedex/track/${encodeURIComponent(trackingNumber)}?detailed=${detailed}`);
  }

  /**
   * Get FedEx tracking info for an outbound order
   */
  async getFedExOutboundTracking(outboundOrderId: string) {
    return this.get(`/fedex/outbound/${encodeURIComponent(outboundOrderId)}/tracking`);
  }

  /**
   * Set FedEx tracking number for an outbound order
   */
  async setFedExOutboundTracking(outboundOrderId: string, trackingNo: string, autoRefresh: boolean = true) {
    return this.post(`/fedex/outbound/${encodeURIComponent(outboundOrderId)}/tracking`, {
      trackingNo,
      autoRefresh,
    });
  }

  /**
   * Refresh FedEx tracking status for an outbound order
   */
  async refreshFedExOutboundTracking(outboundOrderId: string) {
    return this.post(`/fedex/outbound/${encodeURIComponent(outboundOrderId)}/refresh`, {});
  }

  /**
   * Batch refresh FedEx tracking status for multiple outbound orders
   */
  async batchRefreshFedExTracking(outboundOrderIds: string[]) {
    return this.post('/fedex/outbound/batch-refresh', {
      outboundOrderIds,
    });
  }

  /**
   * Get all FedEx status codes with localized names
   */
  async getFedExStatusCodes() {
    return this.get('/fedex/status-codes');
  }

  /**
   * Get FedEx public tracking URL
   */
  async getFedExTrackingUrl(trackingNumber: string) {
    return this.get(`/fedex/tracking-url/${encodeURIComponent(trackingNumber)}`);
  }

  // ==================== FedEx Shipment Creation ====================

  /**
   * Create a FedEx shipment directly
   */
  async createFedExShipment(shipmentRequest: {
    shipper: {
      address: {
        streetLines: string[];
        city: string;
        postalCode: string;
        countryCode: string;
        stateOrProvinceCode?: string;
        residential?: boolean;
      };
      contact: {
        personName?: string;
        companyName?: string;
        phoneNumber: string;
        emailAddress?: string;
      };
      tins?: Array<{ number: string; tinType: string }>;
    };
    recipient: {
      address: {
        streetLines: string[];
        city: string;
        postalCode: string;
        countryCode: string;
        stateOrProvinceCode?: string;
        residential?: boolean;
      };
      contact: {
        personName?: string;
        companyName?: string;
        phoneNumber: string;
        emailAddress?: string;
      };
      tins?: Array<{ number: string; tinType: string }>;
    };
    packages: Array<{
      weight: { units: 'KG' | 'LB'; value: number };
      dimensions?: { units: 'CM' | 'IN'; length: number; width: number; height: number };
      itemDescription?: string;
    }>;
    commodities?: Array<{
      description: string;
      countryOfManufacture: string;
      quantity: number;
      quantityUnits: string;
      unitPrice: { amount: number; currency: string };
      customsValue: { amount: number; currency: string };
      weight: { units: 'KG' | 'LB'; value: number };
      harmonizedCode?: string;
    }>;
    serviceType?: string;
    packagingType?: string;
    pickupType?: string;
    orderReference?: string;
    customerReference?: string;
  }) {
    return this.post('/fedex/shipment', shipmentRequest);
  }

  /**
   * Create a FedEx shipment for an outbound order
   * This gets recipient info from the order and creates a shipment
   */
  async createFedExOutboundShipment(
    outboundOrderId: string,
    shipperInfo: {
      address: {
        streetLines?: string[];
        street?: string;
        city: string;
        postalCode: string;
        countryCode: string;
        stateOrProvinceCode?: string;
      };
      contact: {
        personName?: string;
        companyName?: string;
        phoneNumber: string;
        emailAddress?: string;
      };
      tins?: Array<{ number: string; tinType: string }>;
    },
    options?: {
      serviceType?: string;
      packagingType?: string;
      pickupType?: string;
      configId?: string;
      recipientInfo?: {
        countryCode?: string;
        postalCode?: string;
        city?: string;
        phone?: string;
      };
      // Recorded in the audit log: did the operator confirm a recipient
      // address that differs from the order ("仍然建立")?
      addressMismatchConfirmed?: boolean;
    }
  ) {
    return this.post(`/fedex/outbound/${encodeURIComponent(outboundOrderId)}/shipment`, {
      shipper: shipperInfo,
      serviceType: options?.serviceType,
      packagingType: options?.packagingType,
      pickupType: options?.pickupType,
      recipientInfo: options?.recipientInfo,
      configId: options?.configId,
      addressMismatchConfirmed: options?.addressMismatchConfirmed,
    });
  }

  /**
   * Get FedEx waybill label for an outbound order
   * @param format - 'base64' returns JSON with base64 PDF, 'pdf' returns raw PDF file
   * @param resize - '4x6' to resize to 4x6 inches for thermal printing
   */
  async getFedExOutboundLabel(outboundOrderId: string, format: 'base64' | 'pdf' = 'base64', resize?: '4x6') {
    const resizeParam = resize ? `&resize=${resize}` : '';
    if (format === 'pdf') {
      // Return as blob for direct download
      const response = await fetch(`${API_BASE_URL}/fedex/outbound/${encodeURIComponent(outboundOrderId)}/label?format=pdf${resizeParam}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to download label');
      }
      return response.blob();
    }
    return this.get(`/fedex/outbound/${encodeURIComponent(outboundOrderId)}/label?format=base64${resizeParam}`);
  }

  /**
   * Get FedEx waybill label as HTML for printing (same approach as HKTVmall waybill)
   * Returns HTML with embedded PDF as image, sized for 4x6 thermal label
   */
  async getFedExOutboundLabelHtml(outboundOrderId: string): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/fedex/outbound/${encodeURIComponent(outboundOrderId)}/label-html`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
      },
    });
    if (!response.ok) {
      throw new Error('Failed to get FedEx label HTML');
    }
    return response.text();
  }

  /**
   * Get FedEx commercial invoice PDF for an outbound order
   * Commercial invoices are generated by FedEx when creating shipments with ETD enabled
   * @param format - 'pdf' returns raw PDF blob, 'base64' returns JSON with base64 data
   */
  async getFedExCommercialInvoice(outboundOrderId: string, format: 'pdf' | 'base64' = 'pdf'): Promise<Blob | any> {
    if (format === 'pdf') {
      const response = await fetch(`${API_BASE_URL}/fedex/outbound/${encodeURIComponent(outboundOrderId)}/commercial-invoice?format=pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to download commercial invoice');
      }
      return response.blob();
    }
    return this.get(`/fedex/outbound/${encodeURIComponent(outboundOrderId)}/commercial-invoice?format=base64`);
  }

  // ==================== FedEx ETD (Electronic Trade Documents) ====================

  /**
   * Get ETD workflow options
   */
  async getFedExEtdOptions() {
    return this.get('/fedex/etd-options');
  }

  /**
   * Upload letterhead or signature image for ETD
   * @param file - The image file (PNG or JPEG only)
   * @param imageType - 'LETTERHEAD' (700x50 pixels) or 'SIGNATURE' (240x25 pixels)
   * @param imageIndex - IMAGE_1 through IMAGE_5
   */
  async uploadFedExLetterheadImage(
    file: File,
    imageType: 'LETTERHEAD' | 'SIGNATURE',
    imageIndex: 'IMAGE_1' | 'IMAGE_2' | 'IMAGE_3' | 'IMAGE_4' | 'IMAGE_5' = 'IMAGE_1'
  ) {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('imageType', imageType);
    formData.append('imageIndex', imageIndex);

    const response = await fetch(`${API_BASE_URL}/fedex/etd/upload-letterhead`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to upload image');
    }

    return response.json();
  }

  /**
   * Validate customs data for an outbound order before creating FedEx shipment
   */
  async validateFedExCustomsData(outboundOrderId: string) {
    return this.get(`/fedex/outbound/${encodeURIComponent(outboundOrderId)}/validate-customs`);
  }

  /**
   * Get common HS codes reference
   */
  async getFedExHsCodes() {
    return this.get('/fedex/hs-codes');
  }

  /**
   * Get customs clearance options
   */
  async getFedExCustomsOptions() {
    return this.get('/fedex/customs-options');
  }
}

// Create and export singleton instance
export const apiService = new ApiService(API_BASE_URL);
export default apiService;
